import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { HoldRetentionNotice } from './HoldRetentionNotice';
import { initialTransferLuggage, transferLuggageQuoteDisplay } from '../transferLuggageQuote';
import { transferLuggageSelection, TransferLuggageDraft } from '../transferLuggage';
import { useHoldCountdown } from '../holdCountdown';
import { TransferLuggageSection } from './TransferLuggageSection';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Tooltip,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type {
  CaseResponseDto,
  TravelerResponseDto,
} from '@bandido/shared/dtos';
import { LoadingButton } from '@mui/lab';
import type {
  RuteroConnectorOccupancyLimits,
  RuteroDeclarablePassengerField,
  RuteroOfferOptionsSelection,
  RuteroSelectedSupplement,
  RuteroTicketQuantity,
} from '@bandido/shared/types';
import { RUTERO_HOLD_PASSENGER_MAX } from '@bandido/shared/types';

import { useBreakpoints } from '@frontend/features/common/hooks/useBreakpoints';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetAllCases } from '@frontend/features/cases/queries';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';
import {
  useAllCaseTravelers,
  useCreateCaseTraveler,
  useUpdateCaseTraveler,
} from '@frontend/features/travelers/queries';
import { CaseTravelersModal } from '@frontend/features/travelers/components/CaseTravelersModal';
import { CONFIG } from '@frontend/config';
import {
  apiErrorCode,
  apiHoldAttemptId,
  apiHoldRecoveryConflict,
  apiErrorMessage,
  apiRailProviderHoldExpiresAt,
  apiErrorStatus,
  apiMissingPassengerFields,
  apiTourQuoteMissingRequirements,
  apiUnknownOfferOptionIds,
  BookingModalOffer,
  type OfferOptions,
  fetchTravelBookingById,
  isPricedSearchResult,
  requiresFreshSearch,
  PricedSearchResult,
  RuteroMoney,
  RuteroPassengerPayload,
  TravelBooking,
  useBookingTickets,
  useOfferOptions,
  useOfferOptionsPreview,
  useConnectorCapabilities,
  useConfirmBooking,
  useHoldBooking,
  useRetryHoldBooking,
  useSyncBooking,
  useTravelBookingRefresh,
} from '../queries';
import {
  activeOperationLabelKey,
  bookingConfirmBlock,
  canCancelBooking,
  canConfirmBooking,
  canSyncActiveOperation,
} from '../bookingState';
import {
  confirmValidatedFields,
  declaredFieldsForRequestShape,
  declaredPassengerFields,
  fieldsForRow,
  passengerFieldCells,
  passengerFieldsCellCount,
  passengerFieldsOverLimit,
  passengerFormFields,
  isPassengerFieldUnresolved,
  mergeQuoteScopedMissingPassengerFields,
  missingPassengerFieldsForQuote,
  missingPassengerFieldsBeforeHold,
  passengersSatisfyDeclaration,
  pendingConfirmPassengerFields,
  unresolvedMissingPassengerFields,
  withMissingPassengerFields,
} from '../passengerFields';
import {
  bookingPassengersFromParty,
  bookingPartyRowFields,
  bookingPartyValuesValid,
  cleanPassengersForQuoteAndHold,
  isBookingPartyFieldMissing,
} from '../bookingParty';
import type {
  PassengerFieldLevel,
  QuoteScopedMissingPassengerFields,
  RequestShape,
} from '../passengerFields';
import {
  passengerFieldRefEntries,
  passengerFieldRefLabels,
} from '../passengerFieldLabels';
import { PassengerFieldControl } from './PassengerFieldControl';
import { RouteMapCard } from './RouteMapCard';
import { bookingRouteMapPoints, holdRoutePoints, routeMapPoints } from '../routeMap';
import type { RouteMapPoint } from '../routeMap';
import type { FerryOffer } from '../ferry';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import {
  authoritativeQuoteTokenForHold,
  isOfferPreviewEnabled,
  resolveSelectionPreviewDisplay,
  selectionsEqual,
  selectionPreviewAllowsHold,
  shouldRefreshCircuitQuoteAfterHoldError,
  showUnquotedOptionalItemsPrice,
} from '../offerOptionsPreview';
import {
  offerOptionsRequestFor,
  resolveOfferOptionsRender,
  seatSelectionValid,
} from '../offerOptionsRender';
import {
  offerOptionsPreviewDraft,
  offerOptionsPreviewReady,
  passengersForOfferOptionsPreview,
  stablePreviewPassengers,
  offerOptionsSelectionPayload,
} from '../offerOptionsSelection';
import { formatMoney } from '../money';
import { formatProviderDateTime } from '../format';
import { FareCountdown } from './FareCountdown';
import { HoldCountdown } from './HoldCountdown';
import { BookingConditions, hasBookingConditions } from './BookingConditions';
import { BookingDecisionBar } from './BookingDecisionBar';
import { BookingMomentHeader } from './BookingMomentHeader';
import { BookingSectionDisclosure } from './BookingSectionDisclosure';
import { useBookingTransitionFocus } from '../hooks/useBookingTransitionFocus';
import { OfferOptionalItems } from './OfferOptionalItems';
import {
  changeOptionalItemUnits,
  offerOptionalItems,
  optionalItemCodesForHold,
  optionalItemsHoldError,
  optionalItemsSectionVisible,
  selectedOptionalItemsSummary,
} from '../offerOptionalItems';
import type {
  OptionalItemSelection,
  OptionalItemsHoldError,
} from '../offerOptionalItems';
import { BookingRecord } from './BookingRecord';
import { isQuoteFreshForHold, useFareCountdown } from '../useFareCountdown';
import { FerryOptionsPicker } from './FerryOptionsPicker';
import { BusSeatPicker } from './BusSeatPicker';
import { BookingExtras } from './BookingExtras';
import { BookingTickets } from './BookingTickets';
import { VoucherAction } from './VoucherAction';
import { voucherSurfaceVisible } from '../voucherAction';
import { SelectedBookingOptions } from './SelectedBookingOptions';
import { ConfirmedOptionalItems } from './ConfirmedOptionalItems';
import { confirmedOptionalItemsFallbackVisible } from '../confirmedOptionalItems';
import { BookingLegsSection } from './BookingLegsSection';
import { BookingTripSummaryView } from './BookingTripSummary';
import { OperationalServiceMoments } from './OperationalServiceMoments';
import {
  freezeBookingTripSummary,
  transferSummaryWithFlights,
} from '../bookingTripSummary';
import type {
  BookingTripSummary,
  CarTripSummary,
  TransferTripSummary,
} from '../bookingTripSummary';
import {
  adoptExistingHoldAttempt,
  clearHoldAttemptHolder,
  defaultAttemptIdFactory,
  holdPayloadIdentity,
  holdLogicalPayloadIdentity,
  holdAttemptMatchesLuggage,
  existingLuggageHoldRecovery,
  holdAttemptSubmission,
  resolveHoldAttemptFromHolder,
  shouldResetHoldAttemptAfterError,
} from '../holdAttemptIdentity';
import type { HoldAttemptIdentity } from '../holdAttemptIdentity';
import { ActiveOperationAlert, ActiveOperationChip } from './ActiveOperationStatus';
import { ConfirmPassengerContractWarning } from './ConfirmPassengerContractWarning';
import { useActiveOperationRecheck } from '../activeOperationRecheck';
import { useAudience } from '../audience';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { resolveBookingModalDisplayAuthority } from '../bookingModalDisplayAuthority';
import { preHoldRepriceComparison, resolveBookingPrice } from '../bookingPrice';
import { BookingRepriceNotice } from './BookingRepriceNotice';
import {
  connectorCapabilityVerdict,
  connectorUnavailableReasons,
  resolveConnectorCapabilities,
} from '../connectorCapabilityResolution';
import { effectiveTicketResult, ticketFetchSurfaces } from '../ticket-utils';
import { CancelBookingDialog } from './CancelBookingDialog';
import { ConfirmCloseHeldBookingDialog } from './ConfirmCloseHeldBookingDialog';
import { useBookingCancellation } from '../bookingCancellation';
import { shouldPreloadBookingCancellationPolicy } from '../bookingCancellationPreload';
import { CancellationPolicyPanel } from './CancellationPolicyPanel';
import { TransferOperatingHoursSection } from './TransferOperatingHours';
import { resolveCancellationPolicy } from '../cancellationPolicy';
import {
  changeTourTicketQuantity,
  reconcileTourSession,
  shouldClearTourSessionAfterPreviewError,
  tourOptionsAwaitingPickup,
  tourOptionsPartySize,
  tourPickupSelectionValid,
  tourQuoteAge,
  tourQuoteAgeContext,
  tourQuoteAgeDrafts,
  tourTicketQuantitiesValid,
  tourSessionLabel,
  visibleTourOptions,
} from '../tourSessions';
import { releaseNumberInputOnWheel } from '../numberInput';
import {
  continueTourBookingFlow,
  activeTourQuoteRequirements,
  captureTourQuoteRequirements,
  freezeTourQuote,
  frozenTourOptionsForHold,
  reconcileTourBirthDateAges,
  recoverTourOptionalItemsHoldError,
  reviseTourAvailability,
  startTourBookingVisit,
  tourAvailabilityBlockedIssues,
  tourQuoteMustRefreshAfterHoldError,
  tourPassengerAgeOnServiceDate,
  tourSectionsForStage,
  tourQuoteRequirementsOptionsRequest,
} from '../tourBookingFlow';
import {
  transferOfferBookable,
  transferOfferCoverage,
  transferOperatingHoursForPreHoldDetail,
} from '../transfers';
import type { TransferOffer } from '../transfers';
import type {
  FrozenTourQuote,
  ScopedTourQuoteRequirements,
} from '../tourBookingFlow';
import { TourTicketQuantities } from './TourTicketQuantities';
import { SearchPassengerPicker } from './SearchPassengerPicker';
import {
  COUNT_ONLY_PASSENGER_RULES,
  countOnlyPassengers,
  type SearchPassengerDraft,
} from '../searchPassengers';
import type { SelectedTravelQuote } from '../selectedQuote';
import {
  canResumePreHoldAfterTravelerLinking,
  canStartPreHold,
  isQuoteFromActiveSearch,
  type PreHoldSubmissionAuthority,
} from '../preHoldAuthority';
import {
  bookingRecoverySurface,
  holdErrorRecoverySurface,
  sameAttemptRetryAvailability,
  terminalCardDeclaresOfferDead,
} from '../bookingRecovery';
import type { BookingRecoverySurface } from '../bookingRecovery';
import { BookingRecoveryCard } from './BookingRecoveryCard';
import { HoldTerminalErrorCard } from './HoldTerminalErrorCard';
import { ManualReconciliationPanel } from './ManualReconciliationPanel';
import { resolveBookingMoment } from '../bookingMoment';
import { collapsedBookingSectionsOnMobile, mobileTapTargetSx } from '../bookingMobile';
import { resolveBookingClose } from '../bookingCloseGuard';
import type { BookingCloseIntent } from '../bookingCloseGuard';
import type { BookingMomentSection } from '../bookingMoment';
import { resolveBookingDecision, resolveBookingFlags } from '../bookingDecision';
import {
  bookingPendingQuestions,
  clearQuestionAnswersDraft,
  mandatoryAnswersExceedConfirmLimit,
  questionAnswerPayload,
  readQuestionAnswersDraft,
  saveQuestionAnswersDraft,
  unansweredMandatoryQuestions,
} from '../pendingQuestions';
import type { QuestionAnswersDraft } from '../pendingQuestions';
import { PendingQuestionsSection } from './PendingQuestionsSection';
import { PassengerFieldSlot } from './PassengerFieldSlot';
import { PassengerRow } from './PassengerRow';
import { identityDocumentNeedsSelection, reconcileSavedDocumentDraft } from '../transferIdentityDocument';
import { CircuitQuoteOptions } from './CircuitQuoteOptions';
import {
  authoritativeCircuitQuoteCatalogue,
  circuitOccupancyValid,
  circuitOccupancyCount,
  circuitSupplementsValid,
  emptyCircuitQuoteCatalogue,
  passengersWithCircuitRooms,
  reconcileCircuitSupplements,
} from '../circuitOccupancy';
import {
  committedPartyGroups,
  committedPartySnapshot,
  tourPartyFromCount,
  tourPartyWithQuoteAges,
} from '../committedBookingContext';
import type {
  CommittedPassenger,
  CommittedPartySnapshot,
} from '../committedBookingContext';
import { withAgencyNationality } from '../agencyCountry';
import { useAgencyCountryCode } from '../useAgencyCountryCode';
import {
  bookingCaseSearchFilters,
  eligibleBookingCaseOptions,
} from '../bookingCaseSearch';
import { useBookingCaseContext } from './BookingCaseContext';
import { CreateBookingCaseDialog } from './CreateBookingCaseDialog';
import {
  bookingCaseChangeLocked,
  currentCaseRoster,
} from '../bookingCaseContext';
import {
  bookingCaseDraftScopeChanged,
  bookingContactPassengerIndex,
  canonicalTravelerPatch,
  linkCreatedTravelerToPassengerDraft,
  passengerFromTraveler,
  passengerWithCreatedTraveler,
  pendingTravelerCreations,
  reconcileTravelerAssignments,
  runTravelerCreationsForDraft,
  type ScopedTravelerCreationInput,
  stableTravelerCreationInput,
  travelerCreationFailureIsAmbiguous,
  travelerCreationInput,
  travelerPatchShouldPersistByDefault,
  travelerProfileUpdates,
  travelerAlreadyAssigned,
  withBookingContact,
} from '../bookingTravelerRoster';

interface Props {
  vertical: string;
  quote: SelectedTravelQuote<BookingModalOffer> | null;
  /** Identity of the committed search stage that may currently start a hold. */
  activeSearchIdentity?: string;
  onClose: () => void;
  /**
   * La composición del pedido COMITEADO (`committedParty`): de acá salen las
   * filas y la frase que las declara. Una sola derivación para las dos cosas,
   * compartida por los nueve verticales — antes cada buscador la armaba
   * inline y seis perdían la composición en el camino (#2374).
   */
  party?: CommittedPassenger[];
  /** Selección contractual comprometida antes de abrir el flujo transaccional. */
  initialOfferSelection?: RuteroOfferOptionsSelection;
  /** Topes publicados por el conector dueño de la oferta. */
  occupancyLimits?: RuteroConnectorOccupancyLimits;
  /** Resumen de lo que se reserva (fechas, tramos): una línea por renglón. */
  details?: string[];
  /** Datos estructurados para maquetar y congelar el resumen de autos. */
  carSummary?: CarTripSummary;
  /** Datos estructurados del pedido de traslado; los vuelos se completan acá. */
  transferSummary?: TransferTripSummary;
  /**
   * Hora del tramo de IDA (transfers): editable con re-cotización. NO es la
   * hora de recogida — es la hora conocida en la punta del tramo, que en una
   * ruta con aeropuerto es la del vuelo. La recogida la resuelve el proveedor
   * y se muestra en la oferta y en el post-booking.
   */
  pickupTime?: string;
  /** Hora del tramo de VUELTA (RT): editable con re-cotización. */
  returnPickupTime?: string;
  /**
   * ¿La ruta COMMITEADA toca un aeropuerto? Decide el rótulo de las dos horas:
   * con aeropuerto son horas de VUELO, sin aeropuerto son del servicio. Lo
   * calcula el buscador con la ruta comiteada y se pasa hecho para que el
   * modal no derive la misma cosa por otro camino (§21.9).
   */
  routeTouchesAirport?: boolean;
  /** RT: habilita hora y vuelo del tramo de vuelta. */
  isRoundTrip?: boolean;
  /**
   * Origen y destino del PEDIDO (los de la búsqueda COMMITEADA, nunca los del
   * formulario vivo), para el mapa del recorrido cuando la oferta elegida no
   * trae los que resolvió el proveedor. Sin coordenadas usables no se dibuja
   * nada.
   */
  routePoints?: RouteMapPoint[];
  /**
   * Re-cotiza (SIEMPRE trae una generación nueva del search — nunca sirve la
   * cacheada, que reinstalaría la misma provenance/vencimiento) y devuelve la
   * oferta equivalente, o el motivo por el que NO sustituye: `not_found` (no
   * hay equivalente) o `ambiguous` (hay más de una indistinguible — el
   * contrato no publica un id de producto estable, así que la sustitución
   * automática exige un match ÚNICO). El precio puede cambiar (ej. suplemento
   * nocturno) y se muestra antes de holdear.
   *
   * `currentOffer` es la oferta que el MODAL tiene vigente (la última
   * re-cotizada si ya hubo una): la equivalencia se resuelve contra ella, no
   * contra la selección original del buscador — un segundo re-quote tiene que
   * atarse a lo que el asesor está mirando.
   */
  onRequote?: (
    currentOffer: PricedSearchResult,
    newTime: string,
    newReturnTime?: string,
  ) => Promise<
    SelectedTravelQuote<PricedSearchResult> | 'not_found' | 'ambiguous'
  >;
}

const EMPTY_PAX: RuteroPassengerPayload = {
  type: 'adult',
  firstName: '',
  lastName: '',
};

/**
 * Las filas del formulario a partir de la composición comiteada.
 *
 * El tipo y la nacionalidad vienen del PEDIDO cuando el pedido los tiene; los
 * nombres los completa el asesor. La edad NO se copia al pasajero: el contrato
 * de hold no la transporta (`RuteroPassenger`), así que sólo se usa para
 * rotular la fila. Nunca se inventa una `birthDate` a partir de ella.
 */
/**
 * El flujo de reserva, un MOMENTO a la vez: Revisar (todo lo que se decide
 * antes de tocar al proveedor) → Reservado (la verdad del proveedor, con el
 * reloj corriendo) → Confirmado (documentos y registro) → Cerrado. Nunca el
 * formulario y el resultado juntos: el momento lo decide el estado de la
 * reserva (`bookingMoment.ts`) y no hay vuelta atrás una vez que existe.
 *
 * Al pie, la barra de decisión: el precio autoritativo del momento, el reloj,
 * los flags que cambian la decisión y EL botón — con su motivo al lado cuando
 * está bloqueado (`bookingDecision.ts`).
 *
 * Los pasajeros son FIJOS: salen de los ocupantes de la búsqueda (cambiarla
 * exige re-buscar). Cambiar un horario re-cotiza (el token viejo no vale para
 * otro horario). Un outcome ambiguo se muestra como "pendiente de
 * verificación" con botón de sincronizar — nunca error duro ni retry.
 */
export function BookingModal({
  vertical,
  quote,
  activeSearchIdentity,
  onClose,
  party,
  initialOfferSelection,
  occupancyLimits,
  details,
  carSummary,
  transferSummary,
  routePoints,
  pickupTime,
  returnPickupTime,
  routeTouchesAirport,
  isRoundTrip,
  onRequote,
}: Props) {
  const { t } = useTranslation();
  const isTours = vertical === 'tours';
  const isCircuits = vertical === 'circuits';
  // Cuánto se revela de la composición del precio: el número del proveedor y
  // su desglose son del asesor (con el total al lado dejan leer el margen),
  // el mismo criterio que el registro comercial y las cards.
  const { isAgent } = useAudience();
  // Quién está logueado: el draft de respuestas es SUYO, no de la pestaña
  // (el logout no recarga la app — ver `pendingQuestions.ts`).
  const { myProfile } = useAuth();
  const { addToast } = useSnackBar();
  /**
   * El país con el que arrancan los campos de país: nacionalidad (que se
   * siembra en la fila y por eso VIAJA) y el selector de código telefónico
   * (que no es un valor del pasajero, así que va como default del control).
   */
  const agencyCountry = useAgencyCountryCode();
  /**
   * Mobile: el modal se vuelve sheet a pantalla completa y el repaso ya leído
   * arranca plegado. Las dos cosas salen del MISMO breakpoint (`sm`), que es
   * el que separa el teléfono del resto.
   */
  const { isSmallDown: isMobile } = useBreakpoints();
  const hold = useHoldBooking();
  const retryHold = useRetryHoldBooking();
  const confirm = useConfirmBooking();
  const sync = useSyncBooking();
  const refreshBooking = useTravelBookingRefresh();
  const offerOptions = useOfferOptions(vertical);
  const [settledTourOptions, setSettledTourOptions] = useState<OfferOptions>();
  const {
    data: connectors,
    isFetching: areConnectorCapabilitiesFetching,
    isSuccess: areConnectorCapabilitiesSuccessful,
  } = useConnectorCapabilities();
  const bookingCaseContext = useBookingCaseContext();
  const [caseSearch, setCaseSearch] = useState('');
  const [selectedCase, setSelectedCase] = useState<CaseResponseDto | null>(null);
  const [createCaseOpen, setCreateCaseOpen] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [inlineCreatedCaseId, setInlineCreatedCaseId] = useState<number | null>(null);
  const [inlineTravelerSavePreferences, setInlineTravelerSavePreferences] = useState<
    Record<number, boolean>
  >({});
  const {
    data: casesData,
    isFetching: casesLoading,
    isError: casesError,
  } = useGetAllCases(bookingCaseSearchFilters(CONFIG.AGENCY_ID, caseSearch));
  const caseOptions = useMemo(
    () =>
      eligibleBookingCaseOptions(casesData?.items ?? [], CONFIG.AGENCY_ID),
    [casesData?.items],
  );
  const caseTravelers = useAllCaseTravelers(
    selectedCase?.id ?? 0,
    Boolean(selectedCase),
  );
  const updateTraveler = useUpdateCaseTraveler(selectedCase?.id ?? 0);
  const createTraveler = useCreateCaseTraveler(selectedCase?.id ?? 0);
  const currentCaseTravelers = currentCaseRoster(
    caseTravelers.data,
    caseTravelers.isPreviousData,
  );
  const [savingProfiles, setSavingProfiles] = useState(false);
  const profileSaveInFlightRef = useRef(false);
  const travelerCreationRequestsRef = useRef<Record<number, ScopedTravelerCreationInput>>({});
  const bookingCaseDraftScopeRef = useRef<number | null | undefined>(undefined);
  const livePreHoldAuthorityRef = useRef<PreHoldSubmissionAuthority | null>(null);

  useEffect(() => {
    if (holdAttemptRef.current) return;
    if (profileSaveInFlightRef.current) return;
    if (bookingCaseContext.hasCaseContext && !bookingCaseContext.caseData) return;
    const nextCaseId = bookingCaseContext.caseData?.id ?? null;
    const caseScopeChanged = bookingCaseDraftScopeChanged(
      bookingCaseDraftScopeRef.current,
      nextCaseId,
    );
    bookingCaseDraftScopeRef.current = nextCaseId;
    setSelectedCase(bookingCaseContext.caseData);
    setInlineCreatedCaseId((current) =>
      current === bookingCaseContext.caseData?.id ? current : null);
    if (caseScopeChanged) {
      setInlineTravelerSavePreferences({});
      travelerCreationRequestsRef.current = {};
      setCaseSearch('');
    }
  }, [bookingCaseContext.caseData, bookingCaseContext.hasCaseContext]);

  const [passengers, setPassengers] = useState<RuteroPassengerPayload[]>([
    { ...EMPTY_PAX, isContactPerson: true },
  ]);
  const [profileSavePreferences, setProfileSavePreferences] = useState<
    Record<string, boolean>
  >({});
  useEffect(() => {
    if (!currentCaseTravelers) return;
    const rosterIds = new Set(
      currentCaseTravelers.items.map((traveler) => traveler.id),
    );
    setPassengers((current) =>
      reconcileTravelerAssignments(current, rosterIds));
  }, [currentCaseTravelers]);
  /** Composición capturada junto con la oferta; no sigue al buscador mutable. */
  const [partySnapshot, setPartySnapshot] = useState<CommittedPartySnapshot>(
    () => committedPartySnapshot(null, quote?.offer.provenance ?? null, party),
  );
  /**
   * Qué DRAFT de pasajeros se está cargando. Cambia con cada oferta elegida y
   * con nada más — una re-cotización mueve el precio, no las personas.
   *
   * Es la identidad de los controles: van keyeados por esto, así que al pasar
   * a otra reserva se MONTAN de nuevo y su estado interno arranca limpio. Sin
   * eso, el modal queda montado entre ofertas y lo que un control guarda por
   * dentro —el país del teléfono elegido sin tipear número, que no llega a ser
   * un valor y por eso no viaja en `value`— se filtra a la reserva siguiente
   * con el código de discado de otro país (#2374).
   */
  const [passengerDraft, setPassengerDraft] = useState(0);
  const passengerDraftRef = useRef(0);
  const [booking, setBooking] = useState<TravelBooking | null>(null);
  useHoldCountdown(booking?.state === 'held' ? booking.expiresAt : undefined);
  /**
   * El resumen del viaje TAL COMO ESTABA al enviar el hold. Se congela porque
   * después de reservar es la única descripción del itinerario que no se
   * mueve: el resumen vivo lo arma el buscador desde la búsqueda comiteada, y
   * esa puede cambiar bajo los pies del modal (el asesor navega el historial,
   * o edita y vuelve a buscar detrás del diálogo).
   */
  const [heldTripSummary, setHeldTripSummary] =
    useState<BookingTripSummary | null>(null);
  /**
   * El importe que la barra tenía DELANTE del asesor al enviar el hold. Se
   * congela por el mismo motivo que el resumen del viaje: es la cifra con la
   * que se tomó la decisión de reservar, y es lo único contra lo que se puede
   * contrastar el precio que congeló el proveedor. El total vivo de la oferta
   * sigue moviéndose (re-cotizaciones, selección de opciones), así que leerlo
   * después del hold compararía contra otra cosa.
   */
  const [shownBeforeHold, setShownBeforeHold] = useState<RuteroMoney | undefined>(
    undefined,
  );
  const [holdAttempt, setHoldAttempt] = useState<HoldAttemptIdentity | null>(
    null,
  );
  const holdAttemptRef = useRef<HoldAttemptIdentity | null>(null);
  const caseChangeLocked = bookingCaseChangeLocked(
    Boolean(holdAttempt),
    hold.isLoading,
    retryHold.isLoading,
    savingProfiles,
  );
  useEffect(() => {
    if (caseChangeLocked) {
      setCreateCaseOpen(false);
      setRosterOpen(false);
    }
  }, [caseChangeLocked]);
  const [expired, setExpired] = useState(false);
  /**
   * La identidad del pedido que el operador rechazó. No es un booleano: el
   * bloqueo es de ESE pedido, no de la oferta, así que corregir cualquier cosa
   * que viaje al proveedor lo levanta solo.
   */
  const [refusedRequestIdentity, setRefusedRequestIdentity] =
    useState<string | null>(null);
  /**
   * La cara del rechazo del hold cuando todavía NO hay reserva que mostrar: el
   * reintento seguro (ámbar, con la misma clave) o el rechazo limpio (rojo, con
   * el texto del proveedor). Es estado y no toast a propósito: un estado que
   * decide si se puede reintentar no puede desaparecer solo a los segundos.
   */
  const [holdErrorSurface, setHoldErrorSurface] =
    useState<BookingRecoverySurface | null>(null);
  /** La resolución manual, abierta desde el CTA de su tarjeta. */
  const [resolvingManual, setResolvingManual] = useState(false);
  const [resolvingManualBusy, setResolvingManualBusy] = useState(false);
  // La generación vigente: arranca como la elegida y puede reemplazarse
  // atómicamente por una re-cotización (los pax cargados se preservan).
  const [currentQuote, setCurrentQuote] = useState<
    SelectedTravelQuote<BookingModalOffer> | null
  >(quote);
  // `quote` es la identidad de la apertura. Si el padre la reemplaza, los
  // tickets anteriores quedan inactivos incluso en el render previo al reset
  // de `booking`; una re-cotización interna no cambia esa apertura.
  const ticketsQuery = useBookingTickets(
    booking?.id,
    quote?.offer.provenance,
  );
  const transitionScopeKey =
    currentQuote?.offer.provenance ??
    quote?.offer.provenance ??
    booking?.id ??
    `${vertical}:closed`;
  // Este hook es deliberadamente anterior a los retornos del modal cerrado:
  // SearchForm conserva el componente montado y alterna `quote` entre null y
  // una oferta. Invocarlo después cambiaría la cantidad de hooks al abrir.
  const { headingRef, contentRef } = useBookingTransitionFocus(
    transitionScopeKey,
    booking?.state ?? 'review',
  );
  const currentQuoteAuthorityRef = useRef(
    quote?.offer.provenance ?? null,
  );
  // El reloj ÚNICO de la cotización (§21.9), con sus dos FACETAS declaradas:
  // el display (`status`/`source` — el firmado del proveedor adelanta el
  // aviso cuando lo hay, ADVISORY) y el gate (`freshForHold` — SOLO el TTL
  // local, skew-inmune). Chip, gate del hold y aviso de vencida derivan todos
  // de este mismo countdown. Ver `fareCountdownAt`.
  const countdown = useFareCountdown(
    currentQuote?.quotedAt,
    currentQuote?.offerExpiresAt,
  );
  /**
   * Los puntos del pedido ya saneados y rotulados como tales. Se sanean acá y
   * no en cada lugar porque un punto roto no puede hacer fallar la reserva —
   * el mapa es display, la reserva no.
   */
  const requestedRoutePoints = useMemo(
    () => routeMapPoints(routePoints, 'requested'),
    [routePoints],
  );
  /**
   * El rótulo de cada hora, por tramo y por tipo de ruta. Antes los dos campos
   * usaban la MISMA clave, que era neutra: al volverla "(ida)" el campo de la
   * vuelta quedaba rotulado como ida (§21.18.2 — cambiar una clave compartida
   * exige seguirla hasta todos sus consumidores).
   */
  const timeLabel = (leg: 'outbound' | 'return') =>
    t(
      `travelServices.transfers.${leg === 'return' ? 'destinationTime' : 'originTime'}${
        routeTouchesAirport ? 'Flight' : ''
      }`,
    );
  const [time, setTime] = useState(pickupTime ?? '');
  const [returnTime, setReturnTime] = useState(returnPickupTime ?? '');
  const [requoting, setRequoting] = useState(false);
  // Autos: opt-in a crear la reserva "On Hold" en el proveedor (default OFF:
  // el hold local congela el precio sin generar nada del otro lado).
  const [providerHold, setProviderHold] = useState(false);
  // Ferry: acomodación (una entrada POR PLAZA) y tarifa elegidas.
  /**
   * Lo tipeado para las preguntas del operador, por `code`. Es el draft LOCAL
   * de esta reserva (`pendingQuestions.ts`): las preguntas vuelven con la fila
   * del booking, las respuestas todavía no viajaron a ningún lado.
   */
  const [answers, setAnswers] = useState<QuestionAnswersDraft>({});
  /**
   * El pedido de cierre que está esperando confirmación (Escape, el botón de
   * cerrar o el backdrop con una reserva retenida viva). Null = no hay ninguno.
   */
  const [closeIntent, setCloseIntent] = useState<BookingCloseIntent | null>(null);
  const [accommodationIds, setAccommodationIds] = useState<string[]>(
    initialOfferSelection?.accommodationIds ?? [],
  );
  const [ticketTypeId, setTicketTypeId] = useState(
    initialOfferSelection?.ticketTypeId ?? '',
  );
  const [pickupId, setPickupId] = useState(
    initialOfferSelection?.pickupId ?? '',
  );
  const [sessionId, setSessionId] = useState(
    initialOfferSelection?.sessionId ?? '',
  );
  const [tourAgeInputSessionId, setTourAgeInputSessionId] = useState('');
  const [tourSelectionUnavailable, setTourSelectionUnavailable] = useState(false);
  const [tourUndatedAgeInputs, setTourUndatedAgeInputs] = useState<string[]>(
    () => tourQuoteAgeDrafts(partySnapshot.party),
  );
  const [tourTicketQuantities, setTourTicketQuantities] = useState<
    RuteroTicketQuantity[]
  >(initialOfferSelection?.ticketQuantities ?? []);
  const [circuitOccupancy, setCircuitOccupancy] = useState(
    initialOfferSelection?.occupancy,
  );
  const [circuitSupplements, setCircuitSupplements] = useState<
    RuteroSelectedSupplement[]
  >([]);
  const [circuitPublishedCatalogue, setCircuitPublishedCatalogue] = useState(
    emptyCircuitQuoteCatalogue,
  );
  const [circuitQuoteRefreshId, setCircuitQuoteRefreshId] = useState(() =>
    defaultAttemptIdFactory());
  const [tourBookingFlow, setTourBookingFlow] = useState(() =>
    startTourBookingVisit(defaultAttemptIdFactory));
  const tourVisitQuoteRef = useRef(quote);
  const [frozenTourQuote, setFrozenTourQuote] =
    useState<FrozenTourQuote | null>(null);
  const [scopedTourQuoteRequirements, setScopedTourQuoteRequirements] =
    useState<ScopedTourQuoteRequirements | null>(null);
  /**
   * Adicionales de la oferta elegidos para reservar EN el hold: unidades por
   * `detailBookingCode` (#2383). Pertenece a la oferta: se limpia con ella.
   */
  const [optionalItemSelection, setOptionalItemSelection] =
    useState<OptionalItemSelection>({});
  const [luggageDraft, setLuggageDraft] = useState<{
    provenance: string | undefined;
    value: TransferLuggageDraft;
  } | null>(null);
  const [luggageEditing, setLuggageEditing] = useState(false);
  const [luggageRefreshId, setLuggageRefreshId] = useState(defaultAttemptIdFactory);
  const transferLuggageQuote = vertical === 'transfers'
    ? (currentQuote?.offer as TransferOffer | undefined)?.luggageQuote
    : undefined;
  const hasLuggageQuote = Boolean(transferLuggageQuote);
  const luggageValue = useMemo(
    () =>
      luggageDraft?.provenance === currentQuote?.offer.provenance && luggageDraft
        ? luggageDraft.value
        : initialTransferLuggage(transferLuggageQuote, transferSummary?.requestedLuggage),
    [luggageDraft, currentQuote?.offer.provenance, transferLuggageQuote, transferSummary?.requestedLuggage]
  );
  const liveLuggage = useMemo(() => hasLuggageQuote
    ? transferLuggageSelection(luggageValue) : undefined, [hasLuggageQuote, luggageValue]);
  /**
   * El rechazo del hub que pertenece a la sección de adicionales
   * (`UNKNOWN_OFFER_OPTIONS` sobre códigos de adicionales, u
   * `OPTIONAL_ITEM_LIMIT`), resuelto contra el payload que se mandó. Vive en
   * la sección —no en un toast— y se va cuando el asesor corrige la
   * selección.
   */
  const [optionalItemsError, setOptionalItemsError] =
    useState<OptionalItemsHoldError | null>(null);
  /** Autoridad requerida aprendida de rechazos limpios, ligada a provenance. */
  const [
    quoteScopedMissingPassengerFields,
    setQuoteScopedMissingPassengerFields,
  ] = useState<QuoteScopedMissingPassengerFields | null>(
    quote
      ? mergeQuoteScopedMissingPassengerFields(null, quote.offer.provenance, [])
      : null,
  );
  const applyAuthoritativeBooking = useCallback(
    (updated: TravelBooking) => setBooking(updated),
    [],
  );

  useActiveOperationRecheck(booking, applyAuthoritativeBooking);

  /**
   * El draft de respuestas pertenece a la RESERVA, no a la pantalla: al
   * cambiar de reserva (o quedarse sin ninguna) se carga el de ella, así lo
   * tipeado no puede terminar contestando las preguntas de otra.
   */
  const bookingId = booking?.id;
  const draftOwnerId = myProfile?.id;
  useEffect(() => {
    setAnswers(readQuestionAnswersDraft(draftOwnerId, bookingId));
  }, [draftOwnerId, bookingId]);

  const answerQuestion = useCallback(
    (key: string, value: string) => {
      setAnswers((prev) => {
        const next = { ...prev, [key]: value };
        saveQuestionAnswersDraft(draftOwnerId, bookingId, next);
        return next;
      });
    },
    [draftOwnerId, bookingId],
  );

  useEffect(() => {
    const nextPartySnapshot = committedPartySnapshot(
      partySnapshot,
      quote?.offer.provenance ?? null,
      party,
    );
    setPartySnapshot(nextPartySnapshot);
    setPassengers(bookingPassengersFromParty(nextPartySnapshot.party));
    setProfileSavePreferences({});
    setInlineTravelerSavePreferences({});
    travelerCreationRequestsRef.current = {};
    passengerDraftRef.current += 1;
    setPassengerDraft(passengerDraftRef.current);
    setBooking(null);
    clearHoldAttemptHolder(holdAttemptRef);
    setHoldAttempt(null);
    setExpired(false);
    setRefusedRequestIdentity(null);
    setHoldErrorSurface(null);
    // Una oferta nueva no hereda una pregunta de cierre de la anterior: el
    // buscador deja el modal MONTADO sin oferta, así que el estado sobrevive
    // a cerrarlo.
    setCloseIntent(null);
    setResolvingManual(false);
    setResolvingManualBusy(false);
    setCurrentQuote(quote);
    currentQuoteAuthorityRef.current = quote?.offer.provenance ?? null;
    setTime(pickupTime ?? '');
    setReturnTime(returnPickupTime ?? '');
    setProviderHold(false);
    setAccommodationIds(initialOfferSelection?.accommodationIds ?? []);
    setTicketTypeId(initialOfferSelection?.ticketTypeId ?? '');
    setPickupId(initialOfferSelection?.pickupId ?? '');
    setSessionId(initialOfferSelection?.sessionId ?? '');
    setTourSelectionUnavailable(false);
    setTourAgeInputSessionId('');
    setTourUndatedAgeInputs(tourQuoteAgeDrafts(nextPartySnapshot.party));
    setTourTicketQuantities(initialOfferSelection?.ticketQuantities ?? []);
    setCircuitOccupancy(initialOfferSelection?.occupancy);
    setCircuitSupplements([]);
    setCircuitPublishedCatalogue(emptyCircuitQuoteCatalogue());
    setCircuitQuoteRefreshId(defaultAttemptIdFactory());
    if (tourVisitQuoteRef.current !== quote) {
      tourVisitQuoteRef.current = quote;
      setTourBookingFlow(startTourBookingVisit(defaultAttemptIdFactory));
    }
    setFrozenTourQuote(null);
    setScopedTourQuoteRequirements(null);
    setSettledTourOptions(undefined);
    // Los códigos son de ESTA oferta: una selección heredada de la anterior
    // viajaría al hold como códigos que la nueva no ofrece.
    setOptionalItemSelection({});
    setLuggageDraft(null);
    setLuggageEditing(false);
    setLuggageRefreshId(defaultAttemptIdFactory());
    setOptionalItemsError(null);
    setHeldTripSummary(null);
    setQuoteScopedMissingPassengerFields(
      quote
        ? mergeQuoteScopedMissingPassengerFields(null, quote.offer.provenance, [])
        : null,
    );
    offerOptions.reset();
  }, [quote]);

  const preHoldCapabilityResolution = resolveConnectorCapabilities(connectors, {
    provider: currentQuote?.offer.provider ?? '',
    vertical,
  });
  const offerOptionsAvailable =
    connectorCapabilityVerdict('offerOptions', {
      resolution: preHoldCapabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    }) === 'supported';
  const offerQuoteVerdict = connectorCapabilityVerdict('offerQuote', {
    resolution: preHoldCapabilityResolution,
    isSuccess: areConnectorCapabilitiesSuccessful,
    isFetching: areConnectorCapabilitiesFetching,
  });
  const offerQuoteAvailable = offerQuoteVerdict === 'supported';
  const offerQuoteUnavailableReason = connectorUnavailableReasons(connectors, {
    provider: currentQuote?.offer.provider ?? '',
    vertical,
  }).find((reason) => reason.capability === 'offerQuote');
  const visibleOfferOptions = isTours
    ? visibleTourOptions(offerOptions.data, settledTourOptions)
    : offerOptions.data;
  const learnedTourQuoteRequirements = activeTourQuoteRequirements(
    scopedTourQuoteRequirements,
    currentQuote?.offer.provenance,
    sessionId,
  );
  const effectiveTourSessions = visibleOfferOptions?.sessions?.map((session) =>
    session.id === sessionId && learnedTourQuoteRequirements.length
      ? {
        ...session,
        quoteRequirements: [
          ...new Set([
            ...(session.quoteRequirements ?? []),
            ...learnedTourQuoteRequirements,
          ]),
        ],
      }
      : session);
  const selectedTourSession = isTours
    ? effectiveTourSessions?.find((session) => session.id === sessionId)
    : undefined;
  const tourQuoteAgeInputs = selectedTourSession?.date
    ? passengers.map((passenger) => {
      const age = tourPassengerAgeOnServiceDate(
        passenger.birthDate,
        selectedTourSession.date,
      );
      return age === null ? '' : String(age);
    })
    : tourUndatedAgeInputs;
  const tourAgeContext = isTours
    ? tourQuoteAgeContext(
      tourQuoteAgeInputs,
      passengers.length,
      effectiveTourSessions,
      sessionId,
      tourAgeInputSessionId,
    )
    : { required: false, ages: null };
  const completedOptionsDroppedSelectedSession = Boolean(
    isTours && offerOptions.data && sessionId && !selectedTourSession,
  );
  const tourQuoteRequiresAges = tourAgeContext.required;
  const tourAges = tourAgeContext.ages;
  const tourAgeInputsVisible = Boolean(
    sessionId &&
    (tourQuoteRequiresAges || tourAgeInputSessionId === sessionId),
  );
  const tourPickupValid = tourPickupSelectionValid(
    visibleOfferOptions?.pickups,
    pickupId,
  );
  const tourTicketSelectionValid = tourTicketQuantitiesValid(
    selectedTourSession,
    tourTicketQuantities,
  );
  const tourSelection = useMemo(
    () =>
      offerOptionsSelectionPayload({
        pickupId,
        sessionId,
        ticketQuantities: tourTicketQuantities,
      }),
    [pickupId, sessionId, tourTicketQuantities],
  );

  useEffect(() => {
    offerOptions.reset();
  }, [currentQuote?.offer.provenance]);

  useEffect(() => {
    if (isTours && offerOptions.data) setSettledTourOptions(offerOptions.data);
  }, [isTours, offerOptions.data]);

  useEffect(() => {
    if (sessionId && tourQuoteRequiresAges) {
      setTourAgeInputSessionId(sessionId);
    }
  }, [sessionId, tourQuoteRequiresAges]);

  useEffect(() => {
    const request = offerOptionsRequestFor(currentQuote);
    if (
      !request ||
      !offerOptionsAvailable ||
      completedOptionsDroppedSelectedSession
    ) {
      return;
    }
    offerOptions.mutate({
      ...request,
      ...(isTours && tourOptionsPartySize(passengers.length)
        ? { partySize: tourOptionsPartySize(passengers.length) }
        : {}),
      ...(isTours && tourAges ? { distribution: { ages: tourAges } } : {}),
      ...(isTours && Object.keys(tourSelection).length
        ? { selection: tourSelection }
        : {}),
    });
  }, [
    currentQuote?.offer.provenance,
    offerOptionsAvailable,
    isTours,
    passengers.length,
    tourAges?.join(','),
    tourSelection,
    completedOptionsDroppedSelectedSession,
  ]);

  useEffect(() => {
    if (
      offerOptions.data &&
      sessionId &&
      reconcileTourSession(sessionId, offerOptions.data?.sessions) === ''
    ) {
      setSessionId('');
      setTourAgeInputSessionId('');
      setTourUndatedAgeInputs(tourQuoteAgeDrafts(partySnapshot.party));
      setScopedTourQuoteRequirements(null);
      setTourTicketQuantities([]);
    }
  }, [offerOptions.data, sessionId]);

  /**
   * Los adicionales de la oferta, normalizados UNA vez: la misma lista decide
   * si la sección existe, dibuja las filas y arma los códigos del hold (21.9:
   * lo que se valida y lo que se dibuja salen de una sola derivación).
   */
  const optionalItems = useMemo(
    () => offerOptionalItems(currentQuote?.offer.optionalItems),
    [currentQuote],
  );
  const changeOptionalItemSelection = useCallback(
    (detailBookingCode: string, delta: 1 | -1) => {
      // Corregir la selección es el camino que el rechazo pide: al tocarla,
      // el aviso viejo deja de describir lo elegido.
      setOptionalItemsError(null);
      setOptionalItemSelection((current) =>
        changeOptionalItemUnits(current, optionalItems, detailBookingCode, delta));
    },
    [optionalItems],
  );
  /**
   * Los códigos elegidos, una entrada POR UNIDAD: LA expresión de la
   * selección de adicionales (#2383). La consumen el preview (que la cotiza),
   * la selección viva (que gatea el hold hasta que esa cotización llegue) y
   * el hold (que reserva EXACTAMENTE lo cotizado) — una sola derivación, tres
   * consumidores (21.9/21.23).
   */
  const selectedOptionalItemCodes = useMemo(
    () => optionalItemCodesForHold(optionalItemSelection, optionalItems),
    [optionalItemSelection, optionalItems],
  );

  /**
   * Precio previsualizado con la selección actual: el total con la selección
   * lo cotiza el PROVEEDOR (vía el hub, sin reservar) y el backend le aplica el
   * markup. Acá no se suma nada ni se valida ocupación: se pinta lo que vuelve,
   * y si el operador rechazó la selección (`rejected`) se muestra el motivo y
   * no se habilita el hold. Cotizar es una llamada al proveedor por cambio: la
   * selección va con un debounce corto para no cotizar cada click de un +/-.
   */
  const liveOfferSelection = useMemo<RuteroOfferOptionsSelection>(
    () => ({
      ...tourSelection,
      accommodationIds,
      ticketTypeId: ticketTypeId || undefined,
      sessionId: sessionId || undefined,
      ...(selectedOptionalItemCodes.length
        ? { optionalItemCodes: selectedOptionalItemCodes }
        : {}),
      ...(isCircuits && circuitOccupancy
        ? {
          occupancy: circuitOccupancy,
          ...(circuitSupplements.length
            ? { supplements: circuitSupplements }
            : {}),
        }
        : {}),
    }),
    [
      tourSelection,
      accommodationIds,
      ticketTypeId,
      sessionId,
      selectedOptionalItemCodes,
      isCircuits,
      circuitOccupancy,
      circuitSupplements,
    ],
  );
  const quoteAndHoldPassengers = useMemo(() => {
    const cleaned = cleanPassengersForQuoteAndHold(passengers);
    return isCircuits
      ? passengersWithCircuitRooms(cleaned, circuitOccupancy)
      : cleaned;
  }, [passengers, isCircuits, circuitOccupancy]);
  const previewPassengersRef = useRef<RuteroPassengerPayload[] | undefined>();
  const previewPassengers = stablePreviewPassengers(passengersForOfferOptionsPreview(
    vertical,
    quoteAndHoldPassengers,
  ), previewPassengersRef.current);
  previewPassengersRef.current = previewPassengers;
  /**
   * UNA foto debounced de todo lo que viaja al quote. Antes solo se demoraba
   * la selección y los pasajeros iban vivos: escribir "Sebastián" podía
   * generar una cotización por tecla cuando había opciones/adicionales. Los
   * pasajeros no se recortan porque el contrato permite que el proveedor los
   * use para cotizar; se consolidan con la selección en un único request.
   */
  const livePreviewContext = useMemo(
    () => ({
      ...offerOptionsPreviewDraft(liveOfferSelection, previewPassengers),
      luggage: liveLuggage ?? undefined,
      provenance: currentQuote?.offer.provenance,
    }),
    [
      liveOfferSelection,
      previewPassengers,
      liveLuggage,
      currentQuote?.offer.provenance,
    ],
  );
  const [quotedPreviewContext, setQuotedPreviewContext] = useDebouncedState<{
    selection: RuteroOfferOptionsSelection;
    passengers?: RuteroPassengerPayload[];
    luggage?: NonNullable<typeof liveLuggage>;
    provenance?: string;
  }>({ selection: { accommodationIds: [] } }, 350);
  useEffect(() => {
    setQuotedPreviewContext(livePreviewContext);
  }, [livePreviewContext, setQuotedPreviewContext]);
  const quotedSelection = quotedPreviewContext.selection;
  // Mientras corre el debounce no se cotiza la foto anterior contra la
  // oferta/selección nueva. `useDebouncedState` conserva exactamente el objeto
  // que recibió, así la igualdad de referencia expresa "esta foto ya quedó
  // quieta" sin volver a comparar pasajeros campo por campo.
  const previewContextSettled = offerOptionsPreviewReady(
    true,
    livePreviewContext,
    quotedPreviewContext,
  );
  /**
   * El preview corre cuando hay algo que cotizar: opciones publicadas por el
   * conector (ferry/buses) O adicionales elegidos (#2383) — transfers no
   * publica offer-options pero SÍ cotiza los adicionales por `offers/quote`.
   * Si el proveedor no cotiza (400 del hub), el backend responde
   * `unavailable` y rige la degradación de siempre: se avisa y el hold valida.
   */
  const circuitSelectionValid =
    !isCircuits ||
    (circuitOccupancyValid(circuitOccupancy, occupancyLimits) &&
      circuitSupplementsValid(
        circuitSupplements,
        circuitPublishedCatalogue.supplements,
        circuitOccupancy,
      ));
  const previewEnabled = hasLuggageQuote
    ? offerQuoteAvailable && !!liveLuggage && !booking
    : isTours
      ? tourBookingFlow.stage === 'availability' &&
      offerQuoteAvailable &&
      passengers.length > 0 &&
      tourPickupValid &&
      Boolean(sessionId) &&
      tourTicketSelectionValid &&
      (!tourAgeInputsVisible || Boolean(tourAges)) &&
      !booking
      : isCircuits
        ? offerQuoteAvailable && circuitSelectionValid && !booking
        : isOfferPreviewEnabled({
          offerQuoteAvailable,
          hasOptions: Boolean(visibleOfferOptions),
          hasOptionalItems: selectedOptionalItemCodes.length > 0,
          hasBooking: Boolean(booking),
        });
  const optionsPreview = useOfferOptionsPreview(
    vertical,
    currentQuote?.offer.provenance,
    quotedSelection,
    previewEnabled && previewContextSettled,
    quotedPreviewContext.passengers,
    isTours && tourAges ? { ages: tourAges } : undefined,
    isTours
      ? tourBookingFlow.quoteRefreshId
      : isCircuits
        ? circuitQuoteRefreshId
        : hasLuggageQuote ? luggageRefreshId : undefined,
    isTours ? tourOptionsPartySize(passengers.length) : undefined,
    quotedPreviewContext.luggage,
  );
  // Existing absolute-expiry clock forces a render at expiration. The hold
  // handler also rechecks the actual instant, not the most recent timer tick.
  useHoldCountdown(hasLuggageQuote && !booking ? optionsPreview.data?.expiresAt : undefined);
  const previewMissingTourRequirements = apiTourQuoteMissingRequirements(
    optionsPreview.error,
  );
  useEffect(() => {
    if (!isTours || previewMissingTourRequirements.length === 0) return;
    const next = captureTourQuoteRequirements({
      current: scopedTourQuoteRequirements,
      quoteAuthority: currentQuote?.offer.provenance,
      requestSessionId: quotedSelection.sessionId,
      liveQuoteAuthority: currentQuote?.offer.provenance,
      liveSessionId: sessionId,
      requestMatchesLive: selectionsEqual(liveOfferSelection, quotedSelection),
      requirements: previewMissingTourRequirements,
    });
    if (next === scopedTourQuoteRequirements) return;
    setScopedTourQuoteRequirements(next);
    if (previewMissingTourRequirements.includes('ages')) {
      setTourAgeInputSessionId(sessionId);
    }
    // El error puede revelar un requisito que la respuesta visible de options
    // omitió. Se reconcilia el mismo producto/sesión sin elegir sustitutos.
    const request = tourQuoteRequirementsOptionsRequest({
      quoteAuthority: currentQuote?.offer.provenance,
      requestSessionId: quotedSelection.sessionId,
      liveQuoteAuthority: currentQuote?.offer.provenance,
      liveSessionId: sessionId,
      requestMatchesLive: selectionsEqual(liveOfferSelection, quotedSelection),
      requirements: previewMissingTourRequirements,
      partySize: tourOptionsPartySize(passengers.length),
      ages: tourAges ?? undefined,
      selection: liveOfferSelection,
    });
    if (request && offerOptionsAvailable) {
      offerOptions.mutate(request);
    }
  }, [
    isTours,
    previewMissingTourRequirements.join(','),
    scopedTourQuoteRequirements,
    currentQuote?.offer.provenance,
    quotedSelection,
    liveOfferSelection,
    sessionId,
    offerOptionsAvailable,
    passengers.length,
    pickupId,
    tourAges?.join(','),
  ]);
  useEffect(() => {
    const catalogue = authoritativeCircuitQuoteCatalogue({
      preview: optionsPreview.data,
      selectionMatches: selectionsEqual(liveOfferSelection, quotedSelection),
      isFetching: optionsPreview.isFetching,
      isPreviousData: optionsPreview.isPreviousData,
    });
    if (!isCircuits || !catalogue) return;
    setCircuitPublishedCatalogue(catalogue);
    if (circuitOccupancy) {
      setCircuitSupplements((current) => reconcileCircuitSupplements(
        current,
        catalogue.supplements,
        circuitOccupancy,
      ));
    }
  }, [
    isCircuits,
    optionsPreview.data,
    optionsPreview.isFetching,
    optionsPreview.isPreviousData,
    liveOfferSelection,
    quotedSelection,
    circuitOccupancy,
  ]);
  useEffect(() => {
    if (
      !shouldClearTourSessionAfterPreviewError(
        apiErrorCode(optionsPreview.error),
        quotedSelection.sessionId,
        sessionId,
      )
    ) {
      return;
    }
    // La autoridad rechazó ESTA sesión: se limpia solo ella, se bloquea el
    // hold por falta de sesión y se refrescan las opciones con las mismas
    // edades que produjeron el quote. Un error tardío de otra sesión no entra.
    setTourSelectionUnavailable(true);
    setSessionId('');
    setTourAgeInputSessionId('');
    setScopedTourQuoteRequirements(null);
    setTourTicketQuantities([]);
    const request = offerOptionsRequestFor(currentQuote);
    if (request && offerOptionsAvailable) {
      offerOptions.mutate({
        ...request,
        ...(tourOptionsPartySize(passengers.length)
          ? { partySize: tourOptionsPartySize(passengers.length) }
          : {}),
        ...(tourAges ? { distribution: { ages: tourAges } } : {}),
        ...(pickupId ? { selection: { pickupId } } : {}),
      });
    }
    addToast({
      message: t('travelServices.tours.selectionUnavailable'),
      color: 'warning',
    });
  }, [
    optionsPreview.error,
    quotedSelection.sessionId,
    sessionId,
    currentQuote?.offer.provenance,
    offerOptionsAvailable,
    passengers.length,
    pickupId,
    tourAges?.join(','),
  ]);

  /** Autoridad proactiva publicada por la oferta. */
  const offerDeclaredFields = useMemo(
    () => declaredPassengerFields(currentQuote?.offer),
    [currentQuote],
  );
  const reportedMissingPassengerFields = useMemo(
    () =>
      missingPassengerFieldsForQuote(
        quoteScopedMissingPassengerFields,
        currentQuote?.offer.provenance,
      ),
    [quoteScopedMissingPassengerFields, currentQuote],
  );
  /**
   * Una sola declaración efectiva gobierna controles, required y validación:
   * oferta sanitizada más lo aprendido para esta provenance.
   */
  /**
   * La forma del PEDIDO comiteado. `isRoundTrip` ES la declaración del
   * buscador: si no la manda, la forma es desconocida y no se descarta nada —
   * un vertical que no publica si hay tramo de vuelta no puede borrarle un
   * campo a la oferta.
   */
  const requestShape = useMemo<RequestShape>(
    () => (isRoundTrip === undefined ? {} : { hasReturnLeg: isRoundTrip }),
    [isRoundTrip],
  );
  const { declared: effectiveDeclaredFields, contradictions: shapeContradictions } =
    useMemo(
      () =>
        declaredFieldsForRequestShape(
          withMissingPassengerFields(
            offerDeclaredFields,
            reportedMissingPassengerFields,
          ),
          requestShape,
        ),
      [offerDeclaredFields, reportedMissingPassengerFields, requestShape],
    );
  // El formulario de hold respeta solo la autoridad de pasajeros de la oferta.
  // Las edades del quote son composición no personal y viven en su propio paso.
  const formDeclaredFields = effectiveDeclaredFields;
  /** ¿Esta oferta pide la nacionalidad de alguien? Solo entonces se dibuja. */
  const declaresNationality = [
    ...effectiveDeclaredFields.all,
    ...effectiveDeclaredFields.contact,
  ].some((entry) => entry.field === 'nationality');
  /**
   * El país de la agencia como valor INICIAL de la nacionalidad, por una vía
   * única: este efecto. La query resuelve tarde en una carga fría, así que
   * sembrar también al armar las filas daba dos caminos que podían discrepar;
   * este cubre los dos momentos porque se vuelve a disparar con cada draft.
   *
   * Solo si la oferta DECLARA la nacionalidad, o sea solo si el formulario la
   * dibuja: un valor que nadie ve no se puede corregir y viajaría al proveedor
   * sin que exista el control para desmentirlo. Siembra únicamente lo vacío,
   * sin re-armar las filas — eso borraría los nombres ya cargados.
   */
  const seededNationality = declaresNationality ? agencyCountry : undefined;
  useEffect(() => {
    setPassengers((prev) => withAgencyNationality(prev, seededNationality));
  }, [seededNationality, passengerDraft]);

  const unresolvedReportedMissingPassengerFields = useMemo(
    () =>
      unresolvedMissingPassengerFields(
        reportedMissingPassengerFields,
        passengers,
      ),
    [reportedMissingPassengerFields, passengers],
  );

  /**
   * Ofertas que el proveedor cotiza POR PERSONA y que el backend NO totalizó
   * (cuando totaliza deja el unitario en `unitPrice`): el hold cubre a TODOS
   * los pasajeros, así que lo que se va a cobrar es el unitario por la
   * cantidad. Mostrar el unitario acá es cotizar de menos.
   */
  const perPassenger =
    currentQuote?.offer.pricePerPassenger === true && !currentQuote?.offer.unitPrice;
  const baseCommercialMoney = currentQuote?.offer.pricing
    ? {
      ...currentQuote.offer.pricing.final,
      amountMinor:
        currentQuote.offer.pricing.final.amountMinor *
        (perPassenger ? passengers.length : 1),
    }
    : undefined;
  const requiresAuthoritativeQuote = isTours || isCircuits || hasLuggageQuote;
  // Con opciones (ferry/buses) el total previo al hold es el que calculó el
  // backend para la selección actual; sin preview cotizado, el de la oferta.
  /**
   * El total del preview se pinta SOLO si corresponde a la selección viva y a
   * esta oferta (`keepPreviousData` + debounce pueden dejar en `data` la
   * cotización de otra selección u otra oferta). Mientras se cotiza el hold no
   * sale; sin cotización autoritativa se avisa y el hold la valida.
   */
  const basePreviewDisplay = resolveSelectionPreviewDisplay({
    enabled: previewEnabled,
    contextSettled: previewContextSettled,
    liveSelection: liveOfferSelection,
    quotedSelection,
    preview: optionsPreview.data,
    isFetching: optionsPreview.isFetching,
    isPreviousData: optionsPreview.isPreviousData,
    isError: optionsPreview.isError,
    errorCode: apiErrorCode(optionsPreview.error),
    offerFinalMinor: baseCommercialMoney?.amountMinor,
    requiresAuthoritativeQuote,
  });
  const previewDisplay = hasLuggageQuote
    ? transferLuggageQuoteDisplay(basePreviewDisplay, liveLuggage, optionsPreview.data, Date.now())
    : basePreviewDisplay;
  const liveLuggageAuthority = useRef({ hasLuggageQuote, basePreviewDisplay, liveLuggage, preview: optionsPreview.data });
  liveLuggageAuthority.current = { hasLuggageQuote, basePreviewDisplay, liveLuggage, preview: optionsPreview.data };
  const liveSelectionPreview = previewDisplay.authoritative
    ? optionsPreview.data
    : undefined;
  const quoteTokenForHold = authoritativeQuoteTokenForHold(
    previewDisplay,
    optionsPreview.data,
  );
  const frozenTourOptions = frozenTourOptionsForHold(
    frozenTourQuote,
    reconcileTourBirthDateAges(frozenTourQuote, passengers).ages,
  );
  const activeFrozenTourQuote =
    isTours &&
    tourBookingFlow.stage === 'passengers' &&
    frozenTourOptions
      ? frozenTourQuote
      : null;
  const effectiveQuoteTokenForHold = isTours
    ? activeFrozenTourQuote?.options.quoteToken ??
      (tourBookingFlow.stage === 'availability' ? quoteTokenForHold : undefined)
    : quoteTokenForHold;
  const selectionPreview = activeFrozenTourQuote?.preview ?? liveSelectionPreview;
  const previewSupplementFinal = selectionPreview?.supplement?.final;
  const preHoldCommercialMoney = selectionPreview?.total?.final ??
    (previewDisplay.amountMinor !== undefined && baseCommercialMoney
      ? { ...baseCommercialMoney, amountMinor: previewDisplay.amountMinor }
      : undefined);
  const displayAuthority = resolveBookingModalDisplayAuthority({
    booking,
    currentOffer: currentQuote?.offer ?? null,
    commercialMoney: preHoldCommercialMoney,
    vertical,
    verticalLabel: t(`rutero.verticals.${booking?.vertical ?? vertical}`),
  });
  const shownPriceEligibleForReprice = preHoldRepriceComparison({
    displayed: displayAuthority.commercialMoney,
    selection: liveOfferSelection,
    selectionPriceAuthoritative:
      Boolean(activeFrozenTourQuote) || previewDisplay.authoritative,
  });
  const selectedOptionalItemSummaries = selectedOptionalItemsSummary(
    optionalItemSelection,
    optionalItems,
  );
  const selectedOptionalItemNames = selectedOptionalItemSummaries
    .map(({ name, quantity }) => quantity > 1 ? `${quantity} × ${name}` : name)
    .join(' · ');
  /**
   * El precio del momento, en UNA sola expresión: el importe que encabeza la
   * barra, su leyenda, el número congelado del proveedor y el aviso de
   * reprecio salen todos de acá, así que la barra no puede pintar un precio
   * que el resolver no haya declarado.
   */
  const price = resolveBookingPrice({
    authority: displayAuthority,
    offer: currentQuote?.offer ?? null,
    booking,
    shownBeforeHold,
    isAgent,
  });
  // El proveedor autoritativo puede no emitir documentos (ej. Direct Ferries):
  // pedirle tickets devuelve un 400 pelado, así que la acción se ofrece SOLO
  // con un sí explícito del conector (fail-closed), y el "no" se le explica al
  // agente en vez de que el botón desaparezca sin motivo. Mismo veredicto para
  // el botón de acciones y para el refresh de URLs vencidas de `BookingTickets`.
  const capabilityResolution = resolveConnectorCapabilities(connectors, {
    provider: displayAuthority.capabilityProvider,
    vertical: displayAuthority.vertical,
  });
  const ticketsVerdict = connectorCapabilityVerdict('tickets', {
    resolution: capabilityResolution,
    isSuccess: areConnectorCapabilitiesSuccessful,
    isFetching: areConnectorCapabilitiesFetching,
  });
  const holdAvailable =
    connectorCapabilityVerdict('hold', {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    }) === 'supported';
  const confirmCapabilityAvailable =
    connectorCapabilityVerdict('confirm', {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    }) === 'supported';
  const cancelCapabilityAvailable =
    connectorCapabilityVerdict('cancel', {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    }) === 'supported';
  const syncCapabilityAvailable =
    connectorCapabilityVerdict('sync', {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    }) === 'supported';
  const ticketSurfaces = ticketFetchSurfaces({
    verdict: ticketsVerdict,
    effective: effectiveTicketResult({
      ready: booking?.ticketsReady ?? false,
      tickets: booking?.tickets,
      expiresAt: booking?.ticketsExpiresAt,
    }),
  });
  /**
   * Los extras cuelgan de la reserva YA creada (el hold no tiene canal para
   * pedirlos): la sección se ofrece con un sí explícito del conector, y quién
   * la dibuja no decide nada más — la condición completa vive en
   * `extrasSectionVisible`, adentro del componente que comparten las tres
   * superficies.
   */
  const extrasVerdict = connectorCapabilityVerdict('extras', {
    resolution: capabilityResolution,
    isSuccess: areConnectorCapabilitiesSuccessful,
    isFetching: areConnectorCapabilitiesFetching,
  });
  /**
   * La política se consulta solo con disponibilidad explícita. Un snapshot
   * ausente o refrescando no autoriza otra llamada al proveedor.
   */
  const cancellationPolicyVerdict = connectorCapabilityVerdict(
    'cancellationPolicy',
    {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    },
  );
  /**
   * El momento de CANCELAR: el mismo que el detalle del listado
   * (`bookingCancellation.ts`). Lo que devuelve el cancel es la fila
   * autoritativa —estado y precio vigente— y es la que pinta la pantalla.
   */
  const cancellation = useBookingCancellation({
    booking,
    enquire: cancellationPolicyVerdict === 'supported',
    onBooking: setBooking,
  });
  const cancellationPolicyView = resolveCancellationPolicy({
    policy: cancellation.policy,
    isLoading: cancellation.isLoadingPolicy,
  });

  /** La política de autos y transfers forma parte de la decisión previa al confirm. */
  useEffect(() => {
    if (
      !shouldPreloadBookingCancellationPolicy({
        vertical: booking?.vertical,
        state: booking?.state,
        hasRemoteBooking: Boolean(booking?.ruteroBookingId),
        verdict: cancellationPolicyVerdict,
        asked: cancellation.asked,
      })
    ) {
      return;
    }
    void cancellation.enquirePolicy();
  }, [
    booking?.vertical,
    booking?.state,
    booking?.ruteroBookingId,
    cancellationPolicyVerdict,
    cancellation.asked,
    cancellation.enquirePolicy,
  ]);

  /**
   * EL pedido de hold tal como está la pantalla ahora. Vive acá y no dentro
   * del handler porque no lo usa solo la mutación: su identidad canónica es la
   * que decide la idempotency key y la que responde si el asesor ya corrigió
   * lo que el operador rechazó. Una sola construcción, una sola verdad.
   */
  const holdPayload = useMemo(() => {
    if (!currentQuote || !selectedCase) return null;
    const liveOptions = offerOptionsSelectionPayload({
      ...liveOfferSelection,
      quoteToken: effectiveQuoteTokenForHold,
    });
    return {
      caseId: selectedCase.id,
      vertical,
      provenanceToken: currentQuote.offer.provenance,
      ...(providerHold ? { providerHold: true } : {}),
      // La MISMA construcción que usa el preview, adicionales incluidos: el
      // hold reserva exactamente la selección que se cotizó (#2383, §21.9),
      // sin rama por vertical. Los códigos salen del MISMO memo que cotiza el
      // preview — un código de otra oferta no puede viajar, y sin elección el
      // campo no existe (ausencia ≠ `[]`).
      ...(activeFrozenTourQuote?.options ?? liveOptions),
      // El quote y el hold reciben exactamente el mismo contexto de pasajeros.
      passengers: quoteAndHoldPassengers,
    };
  }, [
    vertical,
    currentQuote,
    selectedCase,
    providerHold,
    liveOfferSelection,
    effectiveQuoteTokenForHold,
    quoteAndHoldPassengers,
    activeFrozenTourQuote,
  ]);

  const timesChanged =
    time !== (pickupTime ?? '') ||
    (Boolean(isRoundTrip) && returnTime !== (returnPickupTime ?? ''));

  useEffect(() => {
    livePreHoldAuthorityRef.current = {
      caseId: selectedCase?.id ?? null,
      payloadIdentity: holdPayload ? holdPayloadIdentity(holdPayload) : null,
      quote: currentQuote,
      activeSearchIdentity,
      requoting,
      scheduleChanged: timesChanged,
      authoritativeQuoteReady:
        !requiresAuthoritativeQuote || Boolean(effectiveQuoteTokenForHold),
    };
  }, [
    activeSearchIdentity,
    currentQuote,
    effectiveQuoteTokenForHold,
    holdPayload,
    requoting,
    requiresAuthoritativeQuote,
    selectedCase?.id,
    timesChanged,
  ]);

  if (!quote) return null;

  const preHoldOffer = booking ? null : currentQuote?.offer ?? null;
  if (!booking && !preHoldOffer) return null;
  const transferCoverage = transferOfferCoverage({
    isRoundTrip: vertical === 'transfers' && Boolean(isRoundTrip),
    legs: (preHoldOffer as TransferOffer | null)?.legs,
  });
  const tripComplete = transferOfferBookable({
    isRoundTrip: vertical === 'transfers' && Boolean(isRoundTrip),
    legs: (preHoldOffer as TransferOffer | null)?.legs,
  });
  // La FACETA de gate del reloj: SOLO el TTL local (skew-inmune). El `status`
  // del mismo countdown es advisory (puede decir "vencida" por el firmado del
  // proveedor con el reloj del navegador corrido) y por eso NO gatea el hold:
  // avisa en el chip; si el vencimiento es real, el backend rechaza con
  // `OFFER_EXPIRED` y la tarjeta ofrece re-cotizar. Ver `fareCountdownAt`.
  const quoteFreshForHold = countdown.freshForHold;
  const quoteFromActiveSearch = isQuoteFromActiveSearch(currentQuote, activeSearchIdentity);
  /**
   * Los puntos que se dibujan ANTES del hold, y los mismos que se guardan al
   * holdear: los que el proveedor resolvió para la oferta elegida cuando la
   * oferta los trae, y si no los del pedido.
   *
   * Los del pedido quedan gateados por `quoteFromActiveSearch` y los resueltos
   * no, a propósito: los primeros salen de la búsqueda vigente y dejan de
   * describir esta oferta si se navegó el historial con el modal abierto; los
   * segundos vienen DENTRO de la oferta, así que la describen siempre — y una
   * re-cotización trae los suyos con ella (21.23.4).
   *
   * `preHoldOffer` es la oferta que holdea `handleHold` (ahí `booking` todavía
   * es null), así que lo que se guarda es exactamente lo que se dibujó.
   */
  const preHoldRoutePoints = bookingRouteMapPoints(
    displayAuthority.vertical,
    preHoldOffer,
    quoteFromActiveSearch ? requestedRoutePoints : [],
  );

  const contactIndex = bookingContactPassengerIndex(passengers);
  const holder = passengers[contactIndex];

  /** Autos: el titular es el CONDUCTOR (rótulo y opción de hold en el proveedor). */
  const isCars = vertical === 'cars';

  /** Ferrys: la travesía puede EXIGIR plaza asignada (aviso propio del vertical). */
  const isFerry = vertical === 'ferry';
  /**
   * Los vuelos de transfers tienen su propio bloque arriba (con los horarios de
   * cada tramo): si la oferta los declara, se validan igual pero no se pintan
   * dos veces.
   *
   * Ese bloque cubre SOLO al titular, así que la supresión es por campo Y fila:
   * con `all.flightNumber` los acompañantes se validan contra el campo y lo
   * necesitan en su fila. Filtrarlo también de las de ellos dejaba el botón de
   * reservar trabado sin salida (21.23.3).
   *
   * El de VUELTA no necesita condición: en un one-way ya no está declarado —lo
   * descartó `declaredFieldsForRequestShape` para dibujar Y para validar—, así
   * que suprimirlo acá solo puede afectar al viaje que sí lo tiene.
   */
  const holderFieldsRenderedApart = new Set<RuteroDeclarablePassengerField>(
    vertical === 'transfers' ? ['flightNumber', 'returnFlightNumber'] : [],
  );
  /**
   * Para los campos que pinta un bloque propio (los vuelos de transfers): con
   * qué nivel los declara la autoridad efectiva, o ninguno si no los pide.
   * Sale de la MISMA lista que arma la grilla, así que un campo dibujado
   * aparte no puede quedar con otro nivel que el que se valida.
   */
  const declaredLevel = (
    field: RuteroDeclarablePassengerField,
    isHolder: boolean,
  ): PassengerFieldLevel | undefined =>
    fieldsForRow(effectiveDeclaredFields, isHolder).find(
      (entry) => entry.field === field,
    )?.level;
  /** Solo lo que todavía falta se marca en rojo; la autoridad reportada queda. */
  const fieldError = (
    field: RuteroDeclarablePassengerField,
    isHolder: boolean,
    passengerIndex?: number,
  ) =>
    isPassengerFieldUnresolved(
      unresolvedReportedMissingPassengerFields,
      field,
      isHolder,
    ) ||
    (passengerIndex !== undefined &&
      isBookingPartyFieldMissing(
        field,
        partySnapshot.party[passengerIndex],
        passengers[passengerIndex],
      ));

  const translatePassengerField = (key: string) => t(
    vertical === 'transfers' && key === 'travelServices.booking.paxFields.passportNumber'
      ? 'travelServices.booking.identityDocument'
      : key,
  );
  const fieldLabels = (fields: RuteroDeclarablePassengerField[]) =>
    fields
      .map((field) => translatePassengerField(`travelServices.booking.paxFields.${field}`))
      .join(', ');
  const pick = (
    entries: { field: RuteroDeclarablePassengerField; level: PassengerFieldLevel }[],
    level: PassengerFieldLevel,
  ) => entries.filter((entry) => entry.level === level).map((e) => e.field);

  /**
   * Qué pide este proveedor, en una línea. Es lo que Rutero publica: el asesor
   * ve por qué se le piden esos datos y no otros, sin abrir el modal a probar.
   * Nombra lo que BLOQUEA reservar y lo recomendado; lo que el proveedor valida
   * al confirmar lo dice su propio aviso, que además explica el momento.
   */
  const passengerFieldsNotice = [
    pick(formDeclaredFields.all, 'quote').length
      ? t('travelServices.booking.requiredForQuote', {
        fields: fieldLabels(pick(formDeclaredFields.all, 'quote')),
      })
      : '',
    pick(effectiveDeclaredFields.all, 'hold').length
      ? t('travelServices.booking.requiredForAll', {
        fields: fieldLabels(pick(effectiveDeclaredFields.all, 'hold')),
      })
      : '',
    pick(effectiveDeclaredFields.contact, 'hold').length
      ? t('travelServices.booking.requiredForContact', {
        fields: fieldLabels(pick(effectiveDeclaredFields.contact, 'hold')),
      })
      : '',
    [
      ...pick(effectiveDeclaredFields.all, 'recommended'),
      ...pick(effectiveDeclaredFields.contact, 'recommended'),
    ].length
      ? t('travelServices.booking.recommendedFields', {
        fields: fieldLabels([
          ...pick(effectiveDeclaredFields.all, 'recommended'),
          ...pick(effectiveDeclaredFields.contact, 'recommended'),
        ]),
      })
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  /** Los que el proveedor valida al confirmar: se cargan igual acá (21.23.2). */
  const confirmValidated = confirmValidatedFields(effectiveDeclaredFields);

  /** Los vuelos del titular, que se dibujan en el bloque de horarios. */
  const outboundFlightLevel = declaredLevel('flightNumber', true);
  const returnFlightLevel = declaredLevel('returnFlightNumber', true);

  const unresolvedFieldLabels = passengerFieldRefLabels(
    unresolvedReportedMissingPassengerFields,
    translatePassengerField,
  );

  /**
   * Las preguntas del operador de esta reserva: UNA lista, de la que salen la
   * sección que se dibuja, el bloqueo del CTA y lo que viaja en el confirm.
   */
  const pendingQuestions = bookingPendingQuestions(booking);
  /** Sin las obligatorias contestadas, confirmar falla del lado del operador. */
  const questionsAnswered =
    unansweredMandatoryQuestions(pendingQuestions, answers).length === 0;

  const passengersValid =
    passengersSatisfyDeclaration(effectiveDeclaredFields, passengers) &&
    bookingPartyValuesValid(partySnapshot.party, passengers);
  const preHoldMissingPassengerFields = missingPassengerFieldsBeforeHold(
    effectiveDeclaredFields,
    passengers,
  );
  /**
   * Valores que EXCEDEN el tope del borde (hoy solo puede pasarle al
   * multilínea, que no recorta en el input a propósito). La misma expresión
   * que pinta el error en el control decide acá: mandarlos es un 400 seguro,
   * así que bloquean el CTA nombrando qué acortar.
   */
  const overLimitFields = passengerFieldsOverLimit(passengers);
  /**
   * Las contradicciones BLOQUEAN reservar. No se dibujan —no hay dónde
   * escribirlas— pero siguen decidiendo, que es la otra mitad de la regla.
   *
   * Las DOS, y no solo las de nivel `hold`. Un campo `confirm` normal no
   * bloquea reservar porque el asesor puede cargarlo ahora: se pide antes del
   * hold justamente porque el hold es el único momento en que los datos del
   * pasajero viajan al hub. Una CONTRADICCIÓN de ese nivel no se puede cargar
   * nunca, así que holdear crearía en el proveedor una reserva que ya sabemos
   * que no se va a poder confirmar, sin canal para completarla después
   * (21.23.3). Que el nivel coincida no los hace el mismo caso.
   */
  const holdBlockingContradictions = shapeContradictions.map(
    (entry) => entry.ref,
  );
  /**
   * Los pasajeros AUTORITATIVOS para lo que falta al confirmar: los que el
   * registro devolvió (lo que viajó al proveedor). Sin ellos —una reserva
   * anterior al campo— se cae a los que se cargaron en el formulario, que es
   * exactamente lo que se envió en el hold de esta pantalla; nunca a ninguno,
   * que reportaría faltante todo lo declarado.
   */
  const recordPassengers = booking?.passengers?.length
    ? booking.passengers
    : passengers;
  /** Confirmar es la decisión VIVA de este momento (contrato + operación). */
  const confirmAvailable = booking ? confirmCapabilityAvailable && canConfirmBooking(booking) : false;
  /**
   * Lo que el proveedor valida AL CONFIRMAR y todavía no está cargado. Una
   * sola expresión: de acá salen la sección "Falta para confirmar" y el motivo
   * que deshabilita el CTA con su lista al lado.
   *
   * Se resuelve solo cuando confirmar es la decisión viva: con la reserva en
   * verificación, con una operación en vuelo o con el bloqueo durable puesto no
   * hay CTA que explicar, y el consejo de la sección (cancelar y reservar de
   * nuevo) tampoco sería el camino — eso lo dicen el estado y su propio aviso.
   */
  const pendingConfirmFields =
    booking && confirmAvailable
      ? pendingConfirmPassengerFields(effectiveDeclaredFields, recordPassengers)
      : [];
  const hasAccommodations = (visibleOfferOptions?.accommodations.length ?? 0) > 0;
  /**
   * Qué se monta con lo que volvió, decidido por la FORMA de la respuesta y no
   * por el vertical: geometría de asiento → el mapa de la unidad; plazas o
   * tarifas sin geometría → la lista; nada que elegir → ninguna sección.
   */
  const optionsRender = resolveOfferOptionsRender({
    options: visibleOfferOptions,
    failure: offerOptions.isError
      ? { status: apiErrorStatus(offerOptions.error) }
      : undefined,
  });
  /**
   * Ferry: la travesía EXIGE plaza asignada (el operador rechaza el booking
   * sin ella — contrato de rutero). El valor de las opciones manda cuando el
   * hub lo mandó (tri-estado: ausente ≠ false); si no, el de la oferta del
   * search. Nunca se deduce de `accommodations[].compulsory`: esa lista puede
   * venir vacía, y es justo el caso que hay que avisar.
   */
  const accommodationCompulsory =
    isFerry &&
    (visibleOfferOptions?.accommodationCompulsory ??
      (currentQuote?.offer as FerryOffer | undefined)?.accommodationCompulsory ??
      false);
  /**
   * Las dos condiciones que puede tener la selección antes del hold: la regla
   * del mapa de asientos (todo-o-nada, atada a lo que se dibuja) y el veredicto
   * del OPERADOR sobre la selección viva — el hold no sale mientras se cotiza
   * ni si él la rechazó; sin cotización autoritativa (`unavailable`) sale igual
   * y el hold la valida.
   */
  const previewAllowsHold = selectionPreviewAllowsHold(
    previewDisplay,
    Boolean(isTours && activeFrozenTourQuote),
  );
  const tourOptionsValid = !isTours ||
    (tourBookingFlow.stage === 'passengers'
      ? Boolean(activeFrozenTourQuote)
      : passengers.length > 0 &&
        Boolean(sessionId) &&
        tourPickupValid &&
        tourTicketSelectionValid &&
        Boolean(effectiveQuoteTokenForHold));
  const circuitPartyValid =
    !isCircuits ||
    Boolean(
      circuitOccupancy &&
      partySnapshot.party.length > 0 &&
      partySnapshot.party.length === circuitOccupancyCount(circuitOccupancy),
    );
  const optionsValid =
    previewAllowsHold &&
    tourOptionsValid &&
    circuitSelectionValid &&
    circuitPartyValid &&
    (!requiresAuthoritativeQuote || Boolean(effectiveQuoteTokenForHold)) &&
    seatSelectionValid({
      picker: optionsRender.picker,
      selectedCount: accommodationIds.length,
      passengers: passengers.length,
    });

  /**
   * El operador rechazó ESTE pedido y todavía no cambió nada. El contrato del
   * hub es explícito: `provider_policy` "no es una falla... reintentar no
   * cambia nada; hay que corregir lo pedido". Así que no se mata la oferta —el
   * formulario se queda, y el ejemplo canónico del contrato (la selección de
   * acomodaciones de ferry) se corrige acá mismo—: se bloquea el reenvío
   * IDÉNTICO, y en cuanto el pedido cambia el CTA vuelve solo, con identidad
   * de intento nueva porque el rechazo fue limpio.
   */
  const requestRefused =
    refusedRequestIdentity !== null &&
    holdPayload !== null &&
    refusedRequestIdentity === holdLogicalPayloadIdentity(holdPayload, liveLuggage ?? undefined);

  /**
   * Que la oferta elegida TODAVÍA se pueda reservar es una sola condición, y
   * de ella salen las dos cosas que dependen de ella: que se dibuje el
   * formulario y que exista el CTA. Como dos expresiones parecidas quedaría un
   * formulario que no lleva a ningún lado, o un botón sin nada que completar.
   */
  const offerHoldable = quoteFromActiveSearch && !expired && quoteFreshForHold;
  const momentView = resolveBookingMoment({ booking, offerHoldable });
  const decision = resolveBookingDecision({
    moment: momentView.moment,
    hold: {
      offered: offerHoldable,
      caseSelected: Boolean(selectedCase),
      passengersValid: passengersValid && !passengers.some((passenger) =>
        identityDocumentNeedsSelection(
          passenger,
          currentCaseTravelers?.items.find((traveler) => traveler.id === passenger.travelerId),
        )),
      tripComplete,
      missingPassengerFields: preHoldMissingPassengerFields,
      fieldsOverLimit: overLimitFields,
      shapeContradictions: holdBlockingContradictions,
      optionsValid,
      luggageQuoteBlocked: hasLuggageQuote && !previewAllowsHold,
      scheduleChanged: timesChanged,
      requoting,
      optionsLoading: offerOptionsAvailable && offerOptions.isLoading,
      requestRefused,
    },
    confirm: {
      available: confirmAvailable,
      questionsAnswered,
      mandatoryAnswersOverflow:
        mandatoryAnswersExceedConfirmLimit(pendingQuestions),
      missingPassengerFields: pendingConfirmFields,
    },
  });
  const isTourAvailabilityStage =
    isTours && !booking && tourBookingFlow.stage === 'availability';
  const isTourPassengerStage =
    isTours && !booking && tourBookingFlow.stage === 'passengers';
  const visibleMomentSections = isTours && !booking
    ? tourSectionsForStage(momentView.sections, tourBookingFlow.stage)
    : momentView.sections;
  const visibleDecisionBlocked = isTourAvailabilityStage
    ? tourAvailabilityBlockedIssues(decision.blocked)
    : decision.blocked;
  const continueToTourPassengers = () => {
    const frozen = freezeTourQuote(
      {
        ...tourSelection,
        accommodationIds,
        ticketTypeId,
        sessionId,
        optionalItemCodes: selectedOptionalItemCodes,
      },
      quoteTokenForHold,
      optionsPreview.data,
      tourAges,
      selectedTourSession?.date,
    );
    if (!frozen) return;
    setFrozenTourQuote(frozen);
    setPartySnapshot((current) => ({
      ...current,
      party: tourPartyWithQuoteAges(current.party, frozen.ages),
    }));
    setTourBookingFlow((current) => continueTourBookingFlow(
      current,
      true,
    ));
  };
  const returnToTourAvailability = () => {
    setFrozenTourQuote(null);
    setTourBookingFlow(() => reviseTourAvailability(defaultAttemptIdFactory));
  };
  const changeTourParty = (next: SearchPassengerDraft[]) => {
    if (!isTours || next.length === passengers.length) return;
    const nextParty = tourPartyFromCount(next.length);
    setPartySnapshot({
      quoteAuthority: currentQuote?.offer.provenance ?? null,
      party: nextParty,
    });
    setPassengers(bookingPassengersFromParty(nextParty));
    setInlineTravelerSavePreferences({});
    travelerCreationRequestsRef.current = {};
    passengerDraftRef.current += 1;
    setPassengerDraft(passengerDraftRef.current);
    setSessionId('');
    setTourAgeInputSessionId('');
    setTourUndatedAgeInputs(tourQuoteAgeDrafts(nextParty));
    setScopedTourQuoteRequirements(null);
    setTourTicketQuantities([]);
    setFrozenTourQuote(null);
    setSettledTourOptions(undefined);
    setTourBookingFlow(() => reviseTourAvailability(defaultAttemptIdFactory));
    offerOptions.reset();
  };
  const flags = resolveBookingFlags(preHoldOffer);

  /**
   * Qué arranca plegado: solo en mobile y solo el repaso ya leído. En desktop
   * la lista es vacía y el flujo se dibuja exactamente como hasta ahora.
   */
  const collapsedSections = isMobile
    ? collapsedBookingSectionsOnMobile(momentView)
    : [];

  /**
   * Cerrar el modal, por donde sea que se pida: Escape, el botón de la barra,
   * el backdrop o el "volver a buscar" de una oferta muerta.
   *
   * Con la reserva retenida y viva se pregunta primero — el reloj corre del
   * lado del proveedor y este modal es donde se ve. Una sola puerta para los
   * cuatro caminos: si el botón cerrara directo y solo Escape preguntara, la
   * misma acción tendría dos comportamientos. El instante se lee al pedir el
   * cierre y no en el render, para que un hold que venció mientras el modal
   * estaba abierto no dispare una pregunta que promete una hora pasada.
   */
  const requestClose = () => {
    const intent = resolveBookingClose({
      moment: momentView.moment,
      expiresAt: booking?.expiresAt,
      now: Date.now(),
    });
    if (intent.kind === 'confirm') {
      setCloseIntent(intent);
      return;
    }
    onClose();
  };

  /**
   * El ancho de cada celda sale de cuántas tiene la fila, no del vertical: una
   * fila de ferry con nacionalidad y otra con cinco campos no pueden
   * repartirse la grilla igual. A partir de seis, la fila envuelve.
   *
   * En mobile no se reparte nada: seis columnas sobre 375 px dan controles de
   * 62 px, donde no entra un apellido ni el rótulo que dice qué va ahí. Cada
   * campo se lleva el renglón entero y la fila crece hacia abajo, que es la
   * dirección que sobra en un teléfono.
   */
  const columnWidth = (fieldsInRow: number) => ({
    xs: 12,
    sm: fieldsInRow <= 4 ? 3 : fieldsInRow === 5 ? 2.4 : 2,
  });
  const tourQuoteBirthDatesActive = Boolean(
    isTours && frozenTourQuote?.ages && frozenTourQuote.serviceDate,
  );
  const passengerRowDeclaredFields = tourQuoteBirthDatesActive &&
    !formDeclaredFields.all.some(({ field }) => field === 'birthDate')
    ? [
      ...formDeclaredFields.all,
      { field: 'birthDate' as const, level: 'quote' as const },
    ]
    : formDeclaredFields.all;
  /**
   * UNA medida para todo el bloque: la misma para el titular, para los
   * acompañantes y para los datos de contacto, que es lo que los hace caer en
   * las mismas columnas. Con dos expresiones —la del titular contaba dos
   * celdas y la de los acompañantes tres— no alineaban en ningún vertical.
   *
   * La fila es siempre rol + nombre + apellido + lo que la oferta declara para
   * TODOS; lo del contacto no entra en la cuenta porque vive en su bloque. Se
   * cuentan CELDAS, no campos: un control compuesto ocupa dos.
   */
  const rowColumns = columnWidth(
    3 + passengerFieldsCellCount(passengerRowDeclaredFields),
  );
  /** El ancho de un campo, con las celdas que le corresponden a su control. */
  const fieldColumns = (field: RuteroDeclarablePassengerField) => ({
    ...rowColumns,
    sm: rowColumns.sm * passengerFieldCells(field),
  });

  const setPax = (index: number, partial: Partial<RuteroPassengerPayload>) => {
    setPassengers((prev) =>
      prev.map((pax, i) => (i === index ? { ...pax, ...partial } : pax)),);
  };

  const visibleFieldsForPassenger = (
    index: number,
    isContact: boolean,
  ) => {
    const visibleFields = new Set<RuteroDeclarablePassengerField>(
      bookingPartyRowFields(
        passengerRowDeclaredFields,
        partySnapshot.party[index],
      ).map(({ field }) => field),
    );
    if (isContact) {
      for (const { field } of formDeclaredFields.contact) {
        visibleFields.add(field);
      }
    }
    return visibleFields;
  };

  const selectTraveler = (
    index: number,
    traveler: TravelerResponseDto | null,
    additionalVisibleFields: readonly RuteroDeclarablePassengerField[] = [],
  ) => {
    setPassengers((current) => current.map((passenger, passengerIndex) => {
      if (passengerIndex !== index) return passenger;
      if (!traveler) return { ...passenger, travelerId: undefined };
      const visibleFields = visibleFieldsForPassenger(
        index,
        index === bookingContactPassengerIndex(current),
      );
      additionalVisibleFields.forEach((field) => visibleFields.add(field));
      const birthDateIsAuthoritative = tourQuoteBirthDatesActive ||
        additionalVisibleFields.includes('birthDate');
      return passengerFromTraveler(passenger, traveler, {
        visibleFields,
        identityDocuments: vertical === 'transfers',
        ...(birthDateIsAuthoritative
          ? { authoritativeFields: new Set(['birthDate'] as const) }
          : {}),
      });
    }));
  };

  const shouldSaveTravelerProfile = (
    traveler: TravelerResponseDto,
    input: NonNullable<ReturnType<typeof canonicalTravelerPatch>>,
  ) => profileSavePreferences[traveler.id] ??
    travelerPatchShouldPersistByDefault(input, traveler);

  /**
   * El resumen del PEDIDO que arma el buscador: una línea por renglón, o el
   * bloque maquetado de los verticales cuyo detalle no entra en renglones.
   */
  const requestSummaryData: BookingTripSummary | null = transferSummary
    ? transferSummaryWithFlights(
      transferSummary,
      holder?.flightNumber,
      holder?.returnFlightNumber,
    )
    : carSummary ??
      (details && details.length > 0
        ? { kind: 'lines', lines: details }
        : null);

  const handleRequote = async () => {
    // La equivalencia se resuelve contra la oferta VIGENTE del modal
    // (`currentQuote`, que una re-cotización anterior ya pudo reemplazar):
    // un segundo re-quote se ata a lo que el asesor está mirando, nunca a la
    // selección original del buscador.
    if (!onRequote || !currentQuote || !isPricedSearchResult(currentQuote.offer)) {
      return;
    }
    setRequoting(true);
    try {
      const updated = await onRequote(
        currentQuote.offer,
        time,
        isRoundTrip ? returnTime : undefined,
      );
      if (updated === 'not_found' || updated === 'ambiguous') {
        // Cada motivo con su aviso (§21.18.4): "no está" y "hay varias
        // indistinguibles" piden lo mismo (elegir de la lista, ya refrescada)
        // pero por razones distintas, y el asesor tiene que saber cuál.
        addToast({
          message: t(
            updated === 'ambiguous'
              ? 'travelServices.booking.requoteAmbiguous'
              : 'travelServices.booking.requoteNotFound',
          ),
          color: 'warning',
        });
      } else if (updated) {
        currentQuoteAuthorityRef.current = updated.offer.provenance;
        // El rechazo anterior era de la cotización anterior: con una nueva, su
        // tarjeta deja de describir lo que hay en pantalla. Incluye el de la
        // sección de adicionales, que describe el payload que ya no existe.
        setHoldErrorSurface(null);
        setOptionalItemsError(null);
        setRefusedRequestIdentity(null);
        // Lo mismo vale para el vencimiento: `expired` invalidó la cotización
        // ANTERIOR (reloj local o `RUTERO_OFFER_EXPIRED` del hub). La nueva
        // generación trae su propio `quotedAt` y su propio vencimiento
        // firmado; dejar el flag puesto bloquearía para siempre una oferta
        // recién cotizada.
        setExpired(false);
        setQuoteScopedMissingPassengerFields(
          mergeQuoteScopedMissingPassengerFields(
            null,
            updated.offer.provenance,
            [],
          ),
        );
        setCurrentQuote(updated);
      }
    } catch {
      addToast({ message: t('travelServices.searchError'), color: 'error' });
    } finally {
      setRequoting(false);
    }
  };

  const handleHold = async () => {
    const recoveringLuggageAttempt = existingLuggageHoldRecovery(
      holdAttemptRef.current,
      holdPayload,
      liveLuggage,
      hasLuggageQuote && holdErrorSurface?.kind === 'recovery' && holdErrorSurface.spec.action === 'retry',
    );
    if (
      booking ||
      !tripComplete ||
      !holdAvailable ||
      (decision.disabled && !recoveringLuggageAttempt) ||
      hold.isLoading || retryHold.isLoading ||
      savingProfiles ||
      profileSaveInFlightRef.current ||
      createTraveler.isLoading ||
      updateTraveler.isLoading
    ) return;
    const now = Date.now();
    if (!recoveringLuggageAttempt && hasLuggageQuote && transferLuggageQuoteDisplay(
      basePreviewDisplay,
      liveLuggage,
      optionsPreview.data,
      now,
    ).holdBlocked) return;
    // Safety boundary: all mutable pre-hold authority is re-read at event time,
    // before a local attempt identity or hold mutation can exist.
    if (
      !canStartPreHold(
        {
          quote: currentQuote,
          activeSearchIdentity,
          requoting,
          scheduleChanged: timesChanged,
        },
        now,
      )
    ) {
      // La revalidación event-time usa el MISMO gate skew-inmune (solo TTL
      // local): el firmado del proveedor no puede invalidar acá — si venció de
      // verdad, el backend lo dice con `OFFER_EXPIRED` y ESO invalida.
      if (currentQuote && !isQuoteFreshForHold(currentQuote.quotedAt, now)) {
        setExpired(true);
      }
      return;
    }
    if (!currentQuote) return;
    if (!recoveringLuggageAttempt && requiresAuthoritativeQuote && !effectiveQuoteTokenForHold) return;
    const holdOffer = currentQuote.offer;
    const submittedQuoteAuthority = holdOffer.provenance;
    // El pedido es el MISMO que la pantalla viene mirando (`holdPayload`): de
    // esa identidad salen la key de idempotencia y la pregunta "¿el asesor
    // corrigió lo que el operador rechazó?". Dos construcciones distintas
    // darían dos respuestas distintas a la misma pregunta.
    if (!holdPayload) return;
    const submittedPassengerDraft = passengerDraftRef.current;
    let holdPayloadForSubmission = holdPayload;
    // Un retry durable repite exactamente el intento ya creado. Los perfiles
    // se actualizan únicamente antes del PRIMER hold, nunca durante recovery.
    if (!holdAttemptRef.current) {
      const submittedAuthority = livePreHoldAuthorityRef.current;
      if (
        !submittedAuthority ||
        submittedAuthority.payloadIdentity !== holdPayloadIdentity(holdPayload)
      ) return;
      const profileUpdates = travelerProfileUpdates(
        passengers,
        currentCaseTravelers?.items ?? [],
        profileSavePreferences,
      );
      const travelerCreations = pendingTravelerCreations(
        passengers,
        inlineTravelerSavePreferences,
        inlineCreatedCaseId === selectedCase?.id,
      );
      const allowedPayloadIdentities = new Set([
        submittedAuthority.payloadIdentity,
      ]);
      if (profileUpdates.length || travelerCreations.length) {
        profileSaveInFlightRef.current = true;
        setSavingProfiles(true);
        let creatingTravelerName = '';
        try {
          for (const update of profileUpdates) {
            const updatedTraveler = await updateTraveler.mutateAsync(update);
            const submittedPassenger = passengers.find((passenger) => passenger.travelerId === update.travelerId);
            if (submittedPassenger) {
              setPassengers((current) => reconcileSavedDocumentDraft(
                current,
                submittedPassenger,
                updatedTraveler,
                submittedPassengerDraft,
                passengerDraftRef.current,
              ));
            }
            if (submittedPassengerDraft !== passengerDraftRef.current) return;
          }
          if (profileUpdates.length) {
            addToast({
              message: t('travelServices.booking.profileUpdated'),
              color: 'success',
            });
          }

          let passengersWithProfiles = passengers;
          const creationDraftStayedCurrent =
            await runTravelerCreationsForDraft(
              travelerCreations,
              submittedPassengerDraft,
              () => passengerDraftRef.current,
              async (creation) => {
                const creationOperation = stableTravelerCreationInput(
                  travelerCreationRequestsRef.current[creation.passengerIndex],
                  creation.input,
                  submittedPassengerDraft,
                  defaultAttemptIdFactory,
                );
                travelerCreationRequestsRef.current[creation.passengerIndex] =
                  creationOperation;
                const creationInput = creationOperation.input;
                creatingTravelerName =
                  `${creationInput.firstName} ${creationInput.lastName}`.trim();
                try {
                  return {
                    createdTraveler: await createTraveler.mutateAsync(
                      creationInput,
                    ),
                    creationOperation,
                  };
                } catch (error) {
                  if (
                    travelerCreationRequestsRef.current[
                      creation.passengerIndex
                    ] === creationOperation &&
                    !travelerCreationFailureIsAmbiguous(apiErrorStatus(error))
                  ) {
                    delete travelerCreationRequestsRef.current[
                      creation.passengerIndex
                    ];
                  }
                  throw error;
                }
              },
              (creation, { createdTraveler, creationOperation }) => {
                if (
                  travelerCreationRequestsRef.current[
                    creation.passengerIndex
                  ] === creationOperation
                ) {
                  delete travelerCreationRequestsRef.current[
                    creation.passengerIndex
                  ];
                }
                passengersWithProfiles = passengersWithProfiles.map(
                  (passenger, passengerIndex) =>
                    passengerIndex === creation.passengerIndex
                      ? passengerWithCreatedTraveler(passenger, createdTraveler)
                      : passenger,
                );
                // Cada éxito es durable aunque falle el siguiente perfil o el
                // hold. Un draft reemplazado no entra a este callback.
                setPassengers((current) =>
                  linkCreatedTravelerToPassengerDraft(
                    current,
                    creation.passengerIndex,
                    createdTraveler,
                    submittedPassengerDraft,
                    passengerDraftRef.current,
                  ));
                const cleanedPassengers = cleanPassengersForQuoteAndHold(
                  passengersWithProfiles,
                );
                holdPayloadForSubmission = {
                  ...holdPayload,
                  passengers: isCircuits
                    ? passengersWithCircuitRooms(
                      cleanedPassengers,
                      circuitOccupancy,
                    )
                    : cleanedPassengers,
                };
                allowedPayloadIdentities.add(
                  holdPayloadIdentity(holdPayloadForSubmission),
                );
              },
            );
          if (!creationDraftStayedCurrent) return;
          if (travelerCreations.length) {
            addToast({
              message: t('travelServices.booking.travelersSavedToCase'),
              color: 'success',
            });
          }
        } catch {
          addToast({
            message: creatingTravelerName
              ? t('travelServices.booking.travelerCreateError', {
                traveler: creatingTravelerName,
              })
              : t('travelServices.booking.profileUpdateError'),
            color: 'error',
          });
          return;
        } finally {
          profileSaveInFlightRef.current = false;
          setSavingProfiles(false);
        }
      }
      const currentAuthority = livePreHoldAuthorityRef.current;
      const expectedPayloadIdentity = holdPayloadIdentity(
        holdPayloadForSubmission,
      );
      if (
        !currentAuthority ||
        !canResumePreHoldAfterTravelerLinking(
          submittedAuthority,
          currentAuthority,
          allowedPayloadIdentities,
          expectedPayloadIdentity,
          Date.now(),
        )
      ) return;
    }
    const luggageAuthority = liveLuggageAuthority.current;
    if (!holdAttemptMatchesLuggage(holdAttemptRef.current, luggageAuthority.liveLuggage ?? undefined)) return;
    if (!recoveringLuggageAttempt && luggageAuthority.hasLuggageQuote && transferLuggageQuoteDisplay(
      luggageAuthority.basePreviewDisplay,
      luggageAuthority.liveLuggage,
      luggageAuthority.preview,
      Date.now(),
    ).holdBlocked) return;
    const nextAttempt = recoveringLuggageAttempt ?? resolveHoldAttemptFromHolder(
      holdAttemptRef,
      holdPayloadForSubmission,
      defaultAttemptIdFactory,
      luggageAuthority.liveLuggage ?? undefined,
    );
    // El primer envío congela también el token de quote. Un refetch puede
    // publicar otro mientras el estado ambiguo ofrece Reintentar, pero ese
    // retry tiene que repetir byte-for-byte el pedido y attemptId originales.
    const submission = holdAttemptSubmission(nextAttempt);
    setHoldAttempt(nextAttempt);
    // La cara del intento ANTERIOR no sobrevive al que arranca: el estado raro
    // que se está reintentando lo vuelve a declarar el resultado de este envío.
    setHoldErrorSurface(null);
    setOptionalItemsError(null);
    // Antes de la mutación: cubre el éxito, el outcome ambiguo y la adopción
    // de un intento en colisión — los tres terminan con una reserva en
    // pantalla, y en los tres el itinerario que corresponde es el que el
    // asesor tenía delante al apretar Reservar.
    setHeldTripSummary(freezeBookingTripSummary(requestSummaryData));
    setShownBeforeHold(shownPriceEligibleForReprice);
    try {
      const created =
        submission.kind === 'durable-retry'
          ? await retryHold.mutateAsync(submission.attemptId)
          : await hold.mutateAsync({
            ...submission.payload,
            // Fuera de `payload` a propósito: `resolveHoldAttemptFromHolder`
            // deriva la identidad del intento de lo que se le pasa, y la
            // idempotency key tiene que identificar la OPERACIÓN. Un dato de
            // display que cambiara rotaría la key y crearía una SEGUNDA reserva
            // en el proveedor ante un reintento del mismo intento lógico.
            // `holdRoutePoints` recorta a los campos que valida el DTO: los
            // puntos dibujados llevan además la dirección con la que se nombra
            // cada pin, que es display y no se persiste.
            ...(preHoldRoutePoints.length
              ? { routePoints: holdRoutePoints(preHoldRoutePoints) }
              : {}),
          });
      // Un estado raro con reserva creada NO se avisa con un toast: la fila es
      // la autoridad y su tarjeta —con su acción— vive en el momento
      // Reservado, donde el asesor la lee todas las veces que necesite.
      setBooking(created);
    } catch (error) {
      const code = apiErrorCode(error);
      if (shouldRefreshCircuitQuoteAfterHoldError(vertical, code)) {
        clearHoldAttemptHolder(holdAttemptRef);
        setHoldAttempt(null);
        setCircuitQuoteRefreshId(defaultAttemptIdFactory());
        addToast({
          message: t('travelServices.circuits.quote.refreshing'),
          color: 'warning',
        });
        return;
      }
      if (isTours && tourQuoteMustRefreshAfterHoldError(code)) {
        const missingRequirements = apiTourQuoteMissingRequirements(error);
        const submittedSessionId = nextAttempt.submittedPayload.sessionId;
        const request = tourQuoteRequirementsOptionsRequest({
          quoteAuthority: submittedQuoteAuthority,
          requestSessionId: submittedSessionId,
          liveQuoteAuthority: currentQuoteAuthorityRef.current,
          liveSessionId: sessionId,
          requestMatchesLive: submittedSessionId === sessionId,
          requirements: missingRequirements,
          partySize: tourOptionsPartySize(passengers.length),
          ages: frozenTourQuote?.ages,
          selection: liveOfferSelection,
        });
        setScopedTourQuoteRequirements((current) =>
          captureTourQuoteRequirements({
            current,
            quoteAuthority: submittedQuoteAuthority,
            requestSessionId: submittedSessionId,
            liveQuoteAuthority: currentQuoteAuthorityRef.current,
            liveSessionId: sessionId,
            requestMatchesLive: submittedSessionId === sessionId,
            requirements: missingRequirements,
          }));
        if (
          submittedSessionId === sessionId &&
          missingRequirements.includes('ages')
        ) {
          setTourAgeInputSessionId(sessionId);
        }
        if (request && offerOptionsAvailable) {
          offerOptions.mutate(request);
        }
        clearHoldAttemptHolder(holdAttemptRef);
        setHoldAttempt(null);
        setFrozenTourQuote(null);
        setTourBookingFlow(() => reviseTourAvailability(defaultAttemptIdFactory));
        addToast({
          message: t('travelServices.tours.quoteMustRefresh'),
          color: 'warning',
        });
        return;
      }
      if (code === 'TOUR_SELECTION_UNAVAILABLE') {
        clearHoldAttemptHolder(holdAttemptRef);
        setHoldAttempt(null);
        setTourSelectionUnavailable(true);
        setSessionId('');
        setTourAgeInputSessionId('');
        setScopedTourQuoteRequirements(null);
        setTourTicketQuantities([]);
        setFrozenTourQuote(null);
        setTourBookingFlow(() =>
          reviseTourAvailability(defaultAttemptIdFactory));
        const request = offerOptionsRequestFor(currentQuote);
        if (request && offerOptionsAvailable) {
          offerOptions.mutate({
            ...request,
            ...(tourOptionsPartySize(passengers.length)
              ? { partySize: tourOptionsPartySize(passengers.length) }
              : {}),
            ...(tourAges ? { distribution: { ages: tourAges } } : {}),
            ...(pickupId ? { selection: { pickupId } } : {}),
          });
        }
        addToast({
          message: t('travelServices.tours.selectionUnavailable'),
          color: 'warning',
        });
        return;
      }
      const collision = apiHoldRecoveryConflict(error);
      if (collision && adoptExistingHoldAttempt(holdAttemptRef, nextAttempt.payloadIdentity, collision.bookingId)) {
        setHoldAttempt(holdAttemptRef.current);
        let adopted = false;
        if (collision.recoveryMode === 'manual' || collision.recoveryMode === 'sync') {
          try {
            setBooking(await fetchTravelBookingById(collision.bookingId));
            adopted = true;
          } catch {
            // The conflict remains authoritative even if the follow-up read
            // fails; retain the original attempt and let the list recover it.
          }
        }
        // Con la fila leída, la tarjeta sale de ella (es la autoridad) y no
        // hace falta decir nada más. Sin fila queda el aviso del modo: sync y
        // manual operan SOBRE la fila, así que no hay acción que ofrecer acá.
        if (adopted) return;
        const surface = holdErrorRecoverySurface({
          code,
          recoveryMode: collision.recoveryMode,
        });
        if (surface) {
          setHoldErrorSurface(surface);
          return;
        }
        const messageKey =
          collision.recoveryMode === 'manual'
            ? 'travelServices.booking.manualNotice'
            : collision.recoveryMode === 'sync'
              ? 'travelServices.booking.pendingVerification'
              : 'travelServices.booking.existingAttemptNotice';
        addToast({ message: t(messageKey), color: 'warning' });
        return;
      }
      // Los estados con cara propia del ENVÍO: el transporte ambiguo (que se
      // reintenta acá mismo, con el holder y el formulario a mano) y los
      // rechazos limpios (rojo, con el motivo del operador).
      const errorSurface = holdErrorRecoverySurface({
        code,
        message: apiErrorMessage(error),
        providerHoldExpiresAt: apiRailProviderHoldExpiresAt(error),
      });
      if (shouldResetHoldAttemptAfterError(code)) {
        clearHoldAttemptHolder(holdAttemptRef);
        setHoldAttempt(null);
      } else if (!errorSurface) {
        // No fue un rechazo limpio ni el reintento seguro: la fila write-ahead
        // existe y puede haber una reserva viva del otro lado (adopción tomada
        // por otra réplica, recuperación local pendiente). Si esa fila tiene
        // cara de recuperación, ELLA manda — con su tarjeta y su acción, y sin
        // el formulario: dejar "Reservar" arriba de una reserva que ya existe
        // es ofrecer una segunda.
        //
        // La fila que se lee tiene que ser la de ESTE intento. Un conflicto de
        // payload nombra el mismo id pero una fila que pertenece a OTRO
        // request: mostrar esa reserva sería mostrarle al asesor un pedido que
        // no es el suyo.
        const attemptId = apiHoldAttemptId(error);
        const row =
          attemptId === nextAttempt.attemptId &&
          code !== 'RUTERO_HOLD_ATTEMPT_PAYLOAD_CONFLICT'
            ? await fetchTravelBookingById(attemptId).catch(() => null)
            : null;
        if (row && bookingRecoverySurface(row)) {
          setBooking(row);
          return;
        }
      }
      // Faltó un dato que el proveedor exige: se marca el campo exacto de la
      // fila exacta y el asesor corrige y reintenta sin recargar. El intento ya
      // quedó liberado arriba (es un rechazo limpio), así que el retry sale con
      // una key nueva.
      const missing = apiMissingPassengerFields(error);
      if (
        missing.length &&
        currentQuoteAuthorityRef.current === submittedQuoteAuthority
      ) {
        setQuoteScopedMissingPassengerFields((current) =>
          mergeQuoteScopedMissingPassengerFields(
            current,
            submittedQuoteAuthority,
            missing,
          ));
      }
      // Invalida la cotización, no sólo el intento: sobre esta oferta ningún
      // reintento puede prosperar, así que el botón de reservar tiene que
      // desaparecer hasta que haya una búsqueda nueva.
      if (requiresFreshSearch(error)) {
        setExpired(true);
      }
      // El rechazo que pertenece a la sección de adicionales, resuelto contra
      // el payload QUE VIAJÓ (no contra el estado actual del form, 21.23.4):
      // vive en la sección, donde se corrige, no en un toast que se va solo.
      const addOnsError = optionalItemsHoldError({
        code,
        message: apiErrorMessage(error),
        unknownIds: apiUnknownOfferOptionIds(error),
        sentCodes: nextAttempt.submittedPayload.optionalItemCodes ?? [],
        items: optionalItems,
      });
      // La cara del envío queda EN la pantalla, no en un toast que se va solo:
      // el reintento porque es la acción segura, el rechazo limpio porque el
      // motivo del operador es lo único accionable que hay. Lo que no tiene
      // cara propia —ni fila que adoptar— sigue siendo un toast de error.
      if (errorSurface) {
        setHoldErrorSurface(errorSurface);
        // El operador rechazó lo pedido: mandarlo igual no cambia nada (lo dice
        // el contrato del hub), así que el CTA queda bloqueado con su motivo
        // hasta que el pedido cambie. La oferta NO muere: corregir es el
        // camino, y el formulario tiene que seguir ahí para poder hacerlo.
        if (
          errorSurface.kind === 'terminal' &&
          errorSurface.spec.blocksUnchangedRequest
        ) {
          setRefusedRequestIdentity(nextAttempt.logicalPayloadIdentity);
        }
      } else if (addOnsError) {
        if (isTours) {
          // La selección rechazada está congelada en el segundo paso. Volver
          // al primero hace visible el error junto a los controles, conserva
          // los pasajeros y exige un quote nuevo antes de otro hold.
          const recovery = recoverTourOptionalItemsHoldError(
            addOnsError,
            defaultAttemptIdFactory,
          );
          setOptionalItemsError(recovery.error);
          setFrozenTourQuote(recovery.frozenQuote);
          setTourBookingFlow(recovery.flow);
        } else {
          setOptionalItemsError(addOnsError);
        }
      } else if (!requiresFreshSearch(error)) {
        addToast({
          message: apiErrorMessage(error) ?? t('travelServices.booking.holdError'),
          color: 'error',
        });
      }
    }
  };

  const run = async (
    action: () => Promise<TravelBooking>,
    errorKey: string,
    bookingId: string,
  ) => {
    try {
      const updated = await action();
      setBooking(updated);
    } catch (error) {
      addToast({ message: apiErrorMessage(error) ?? t(errorKey), color: 'error' });
      await refreshBooking(bookingId, setBooking);
    }
  };

  const busy =
    hold.isLoading ||
    retryHold.isLoading ||
    confirm.isLoading ||
    cancellation.isCancelling ||
    sync.isLoading ||
    savingProfiles ||
    resolvingManualBusy;
  /**
   * La cara del estado de la reserva creada. Sale de la fila, que es la
   * autoridad: la misma tarjeta —y la misma acción— que muestra el detalle
   * desde el listado.
   */
  const recoverySurface = booking ? bookingRecoverySurface(booking) : null;
  const syncBooking = (bookingId: string) =>
    run(
      () => sync.mutateAsync(bookingId),
      'travelServices.booking.syncError',
      bookingId,
    );
  /**
   * El motivo por el que el reintento seguro NO se puede disparar desde donde
   * se está mirando, o nada si se puede. Una sola definición para las dos
   * tarjetas que lo pueden pedir (la del envío y la de una reserva creada).
   */
  const retryBlockedNote = (canResendHold: boolean) => {
    const availability = sameAttemptRetryAvailability({ canResendHold });
    return availability.enabled ? undefined : t(availability.noteKey);
  };

  /**
   * El itinerario nunca desaparece con la reserva creada: manda el congelado
   * al reservar y, si no hay, el vivo. Lo que el proveedor CONFIRMÓ se muestra
   * aparte y rotulado como tal, así que las dos cosas no se confunden.
   */
  const tripSummary = (
    <BookingTripSummaryView
      summary={momentView.hasBooking && heldTripSummary
        ? heldTripSummary
        : requestSummaryData}
    />
  );

  /**
   * De dónde sale el número de la barra. Son aclaraciones del momento de
   * Revisar: post-hold el precio es el que congeló el proveedor y no se
   * multiplica ni se le suma nada acá.
   */
  const unquotedOptionalItemsPrice = showUnquotedOptionalItemsPrice({
    isPostHold: displayAuthority.isPostHold,
    hasOptionalItems: selectedOptionalItemCodes.length > 0,
    offerQuoteAvailable,
    display: previewDisplay,
  });
  const preHoldPriceDetails = !displayAuthority.isPostHold && preHoldOffer && (
    <>
      {unquotedOptionalItemsPrice && (
        <Typography variant="caption" color="warning.main" display="block">
          {t('travelServices.booking.basePriceWithUnquotedOptions')}
        </Typography>
      )}
      {perPassenger && passengers.length > 1 && preHoldOffer.pricing &&
        !(isTours && selectionPreview?.total?.final) && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t('travelServices.booking.perPassengerTimes', {
            price: formatMoney(preHoldOffer.pricing.final),
            count: passengers.length,
          })}
        </Typography>
      )}
      {/* De dónde sale la diferencia con el precio del listado: el adicional
          que el backend cotizó para la selección. */}
      {previewSupplementFinal && previewSupplementFinal.amountMinor !== 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t(
            hasLuggageQuote
              ? 'travelServices.transfers.luggage.pricedSelection'
              : previewSupplementFinal.amountMinor > 0
                ? selectedOptionalItemNames
                  ? 'travelServices.booking.includesNamedOptions'
                  : 'travelServices.booking.includesOptions'
                : 'travelServices.booking.discountsOptions',
            {
              // Formato del signo, no cálculo: el importe es el del backend.
              price: formatMoney({
                amountMinor: Math.abs(previewSupplementFinal.amountMinor),
                currency: previewSupplementFinal.currency,
              }),
              options: selectedOptionalItemNames,
            },
          )}
        </Typography>
      )}
      {/* El total de arriba no es el de la selección viva: se está cotizando
          (hold bloqueado) o no se pudo cotizar (el hold la valida). Nunca se
          deja pasar por "el total con las opciones". */}
      {!unquotedOptionalItemsPrice && (previewDisplay.notice === 'quoting' ||
        previewDisplay.notice === 'unavailable') && (
        <Typography
          variant="caption"
          color={previewDisplay.notice === 'quoting' ? 'text.secondary' : 'warning.main'}
          display="block"
        >
          {t(
            hasLuggageQuote
              ? `travelServices.transfers.luggage.${previewDisplay.notice}`
              : previewDisplay.notice === 'quoting'
                ? 'travelServices.booking.quotingSelection'
                : 'travelServices.booking.selectionNotQuoted',
          )}
        </Typography>
      )}
      {/* Lo que quedaría reservado según el proveedor: lo que él dice, no lo
          pedido (la plaza default si no se eligió nada). */}
      {selectionPreview?.state === 'priced' &&
        selectionPreview.selectedOptions &&
        selectionPreview.selectedOptions.accommodations.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t('travelServices.booking.willReserve', {
            options: selectionPreview.selectedOptions.accommodations
              .map((item) => (item.quantity > 1 ? `${item.quantity} × ${item.name}` : item.name))
              .join(', '),
          })}
        </Typography>
      )}
      {selectionPreview?.message && (
        <Typography variant="caption" color="text.secondary" display="block">
          {selectionPreview.message}
        </Typography>
      )}
      {isTours && selectionPreview?.validUntil && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t('travelServices.tours.quoteValidUntil', {
            date: formatProviderDateTime(selectionPreview.validUntil),
          })}
        </Typography>
      )}
    </>
  );

  /**
   * Lo que la barra dice DEBAJO del importe, en el orden en que se lee: qué
   * es ese número en este momento y de dónde sale. Sale todo de `price`, la
   * misma expresión que decide el importe de arriba.
   */
  const priceDetails = (
    <>
      {/* El proveedor avisa que su precio de búsqueda no es el reservable
          (`priceIsIndicative`): se rotula ANTES de reservar, pegado al
          importe, para que el salto del hold no llegue de sorpresa. */}
      {price.estimated && (
        <Typography variant="caption" color="warning.main" display="block">
          {t('travelServices.booking.priceEstimated')}
        </Typography>
      )}
      {/* Con la reserva creada, de quién es el número de arriba. Si el
          congelado del proveedor ES el total, la leyenda alcanza; si difiere
          (el total lleva el markup de la agencia), se muestra también el
          número del proveedor en vez de dejar la leyenda mintiendo. */}
      {price.quoted && (
        <Typography variant="caption" color="text.secondary" display="block">
          {price.quotedIsTotal
            ? t('travelServices.booking.providerFrozenPrice')
            : t('travelServices.booking.providerFrozenPriceAmount', {
              price: formatMoney(price.quoted),
            })}
        </Typography>
      )}
      {preHoldPriceDetails}
    </>
  );

  /**
   * Una sección se dibuja SOLO si el momento la declara, y en el orden en que
   * la declara: la lista de `resolveBookingMoment` es la única definición de
   * qué se ve. Una sección sin contenido devuelve `null` y no deja hueco.
   */
  const renderSection = (section: BookingMomentSection): ReactNode => {
    switch (section) {
    case 'reprice':
      // Sin reprecio no hay sección: `BookingRepriceNotice` devuelve null y no
      // deja hueco (el aviso solo existe si el congelado difiere del importe
      // con el que el asesor decidió reservar).
      return <BookingRepriceNotice price={price} />;

    case 'trip': {
      /* El recorrido, en el mismo bloque de datos del viaje: el asesor
         confirma con el ojo que el punto de recogida es el que cree ANTES de
         holdear. Con la reserva ya creada mandan los puntos que quedaron
         guardados en ella (registro autoritativo, y son los que se dibujaron
         al reservar); los de la búsqueda solo cubren las filas que no los
         tienen. */
      const map = booking ? (
        <OperationalServiceMoments booking={booking} />
      ) : (
        <RouteMapCard
          vertical={displayAuthority.vertical}
          points={preHoldRoutePoints}
        />
      );
      const operatingHours = transferOperatingHoursForPreHoldDetail({
        vertical,
        hasBooking: Boolean(booking),
        value: preHoldOffer?.operatingHours,
      });
      // Sin resumen no hay encabezado suelto ni hueco: queda el recorrido, que
      // trae el suyo y se autooculta si tampoco es dibujable.
      if (!tripSummary && operatingHours.length === 0) return map;
      return (
        <BookingSectionDisclosure
          title={t('travelServices.booking.tripSection')}
          collapsed={collapsedSections.includes('trip')}
        >
          {tripSummary}
          {!booking && hasLuggageQuote && (
            <TransferLuggageSection
              value={luggageValue}
              editing={luggageEditing || !liveLuggage}
              locked={Boolean(holdAttempt)}
              onEdit={() => { if (!holdAttemptRef.current) setLuggageEditing(true); }}
              onChange={(value) => {
                if (!holdAttemptRef.current) setLuggageDraft({ provenance: currentQuote?.offer.provenance, value });
              }}
              preview={liveSelectionPreview}
              notice={previewDisplay.notice}
              onRetry={() => setLuggageRefreshId(defaultAttemptIdFactory())}
              isAgent={isAgent}
            />
          )}
          <TransferOperatingHoursSection windows={operatingHours} />
          {map}
        </BookingSectionDisclosure>
      );
    }

    case 'schedule':
      if (vertical !== 'transfers') return null;
      return (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('travelServices.booking.flightsSection')}
          </Typography>
          {/* Al TOPE, no al centro: el campo de vuelo lleva el renglón del
              badge y el de hora no, así que centrar bajaba el input de hora
              respecto del de vuelo. Con el renglón reservado en las dos celdas
              y la alineación al tope, los dos comparten línea de base (#2374). */}
          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={1.5}>
              <Typography variant="body2" color="text.secondary">
                {t('travelServices.booking.outbound')}
              </Typography>
            </Grid>
            <Grid item xs={3.5}>
              <PassengerFieldSlot>
                <TextField
                  type="time"
                  size="small"
                  fullWidth
                  label={timeLabel('outbound')}
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!onRequote}
                />
              </PassengerFieldSlot>
            </Grid>
            <Grid item xs={3.5}>
              {/* El vuelo del titular vive acá, junto al horario de su tramo,
                    pero se declara y se valida como cualquier otro campo de
                    pasajero: lleva el badge de SU nivel, no uno inventado. */}
              <PassengerFieldSlot level={outboundFlightLevel}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('travelServices.booking.arrivalFlight')}
                  placeholder="IB6012"
                  required={
                    outboundFlightLevel === 'hold' ||
                    outboundFlightLevel === 'confirm'
                  }
                  error={fieldError('flightNumber', true)}
                  value={holder?.flightNumber ?? ''}
                  onChange={(event) =>
                    setPax(contactIndex, {
                      flightNumber: event.target.value.toUpperCase(),
                    })
                  }
                  inputProps={{ maxLength: 12 }}
                />
              </PassengerFieldSlot>
            </Grid>
            <Grid item xs={3.5} />
            {isRoundTrip && (
              <>
                <Grid item xs={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    {t('travelServices.booking.return')}
                  </Typography>
                </Grid>
                <Grid item xs={3.5}>
                  <PassengerFieldSlot>
                    <TextField
                      type="time"
                      size="small"
                      fullWidth
                      label={timeLabel('return')}
                      value={returnTime}
                      onChange={(event) => setReturnTime(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={!onRequote}
                    />
                  </PassengerFieldSlot>
                </Grid>
                <Grid item xs={3.5}>
                  <PassengerFieldSlot level={returnFlightLevel}>
                    <TextField
                      size="small"
                      fullWidth
                      label={t('travelServices.booking.returnFlight')}
                      placeholder="IB6013"
                      required={
                        returnFlightLevel === 'hold' ||
                        returnFlightLevel === 'confirm'
                      }
                      error={fieldError('returnFlightNumber', true)}
                      value={holder?.returnFlightNumber ?? ''}
                      onChange={(event) =>
                        setPax(contactIndex, {
                          returnFlightNumber: event.target.value.toUpperCase(),
                        })
                      }
                      inputProps={{ maxLength: 12 }}
                    />
                  </PassengerFieldSlot>
                </Grid>
                <Grid item xs={3.5} />
              </>
            )}
          </Grid>
          {timesChanged && onRequote && (
            <Box mt={1}>
              <LoadingButton
                size="small"
                variant="outlined"
                loading={requoting}
                onClick={handleRequote}
              >
                {t('travelServices.booking.requote')}
              </LoadingButton>
              <Typography variant="caption" color="text.secondary" ml={1}>
                {t('travelServices.booking.requoteHint')}
              </Typography>
            </Box>
          )}
        </Box>
      );

    case 'offerOptions':
      if (isTourPassengerStage) {
        const frozenOptions = activeFrozenTourQuote?.options;
        const pickup = visibleOfferOptions?.pickups?.find(
          (candidate) => candidate.id === frozenOptions?.pickupId,
        );
        const summarySession = visibleOfferOptions?.sessions?.find(
          (candidate) => candidate.id === frozenOptions?.sessionId,
        );
        const tickets = (frozenOptions?.ticketQuantities ?? [])
          .map((entry) => {
            const option = summarySession?.ticketOptions?.find(
              (candidate) => candidate.id === entry.id,
            );
            return option ? `${entry.quantity} × ${option.name}` : null;
          })
          .filter((entry): entry is string => Boolean(entry));
        const addOns = optionalItems
          .map((item) => {
            const units = frozenOptions?.optionalItemCodes?.filter(
              (code) => code === item.detailBookingCode,
            ).length ?? 0;
            return units > 0 ? `${units} × ${item.name}` : null;
          })
          .filter((entry): entry is string => Boolean(entry));
        return (
          <Box
            border={1}
            borderColor="divider"
            borderRadius={1}
            p={1.5}
          >
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle2">
                  {t('travelServices.tours.quotedSelection')}
                </Typography>
                {summarySession && (
                  <Typography variant="body2">
                    {tourSessionLabel(summarySession)}
                  </Typography>
                )}
                {pickup && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {`${t('travelServices.tours.pickup')}: ${pickup.name}`}
                  </Typography>
                )}
                {tickets.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tickets.join(' · ')}
                  </Typography>
                )}
                {addOns.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {`${t('travelServices.booking.optionalItems.section')}: ${addOns.join(' · ')}`}
                  </Typography>
                )}
              </Box>
              <Button size="small" onClick={returnToTourAvailability}>
                {t('travelServices.tours.changeAvailability')}
              </Button>
            </Box>
          </Box>
        );
      }
      return (
        <>
          {isCircuits &&
            areConnectorCapabilitiesSuccessful &&
            !areConnectorCapabilitiesFetching &&
            offerQuoteVerdict !== 'supported' && (
            <Alert severity="warning">
              {offerQuoteUnavailableReason
                ? t(
                  `travelServices.circuits.quote.unavailableReason.${offerQuoteUnavailableReason.code}`,
                )
                : t('travelServices.circuits.quote.unavailable')}
            </Alert>
          )}
          {isCircuits && circuitOccupancy && occupancyLimits && (
            <CircuitQuoteOptions
              occupancy={circuitOccupancy}
              limits={occupancyLimits}
              preview={liveSelectionPreview}
              categories={circuitPublishedCatalogue.categories}
              publishedSupplements={circuitPublishedCatalogue.supplements}
              supplements={circuitSupplements}
              onOccupancyChange={setCircuitOccupancy}
              onSupplementsChange={setCircuitSupplements}
            />
          )}
          {isCircuits && !circuitPartyValid && (
            <Alert severity="warning">
              {t('travelServices.circuits.quote.ageBandsUnavailable')}
            </Alert>
          )}

          {/* Autos: el hold puede ser sólo local (precio congelado) o una
                reserva "On Hold" real en el proveedor, sin cargo. */}
          {isCars && (
            <Tooltip title={t('travelServices.booking.providerHoldTooltip')}>
              <FormControlLabel
                sx={{ alignSelf: 'flex-start' }}
                control={
                  <Switch
                    size="small"
                    checked={providerHold}
                    onChange={(event) => setProviderHold(event.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('travelServices.booking.providerHold')}
                  </Typography>
                }
              />
            </Tooltip>
          )}

          {/* Lo que la oferta deja elegir sale de la FORMA de la respuesta,
                nunca del vertical: quién publica opciones lo decide el
                conector. Tours conserva su última respuesta completa mientras
                recarga composición o selección; los demás flujos mantienen el
                comportamiento previo. */}
          {visibleOfferOptions && optionsRender.picker === 'seatMap' && (
            <BusSeatPicker
              options={visibleOfferOptions}
              passengers={passengers.length}
              offerClassCode={preHoldOffer?.seatClass?.code}
              accommodationIds={accommodationIds}
              onAccommodationIdsChange={setAccommodationIds}
              ticketTypeId={ticketTypeId}
              onTicketTypeChange={setTicketTypeId}
            />
          )}

          {/* El aviso de plaza exigida vive FUERA del picker: el hub puede
                exigir plaza sin publicar ninguna (listas vacías, o solo
                tarifas), y es justo ahí donde más hay que avisar. Sin plazas
                para elegir no se bloquea el hold: no hay forma de cumplir, y
                el operador es quien decide. */}
          {accommodationCompulsory && !offerOptions.isLoading && (
            <Alert severity={hasAccommodations ? 'info' : 'warning'}>
              {t(
                hasAccommodations
                  ? 'travelServices.ferry.compulsoryNotice'
                  : 'travelServices.ferry.compulsoryNoOptions',
              )}
            </Alert>
          )}

          {visibleOfferOptions && optionsRender.picker === 'optionsList' && (
            <FerryOptionsPicker
              options={visibleOfferOptions}
              passengers={passengers.length}
              accommodationRequired={accommodationCompulsory && hasAccommodations}
              accommodationIds={accommodationIds}
              onAccommodationIdsChange={setAccommodationIds}
              ticketTypeId={ticketTypeId}
              onTicketTypeChange={setTicketTypeId}
            />
          )}

          {isTourAvailabilityStage && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('travelServices.booking.passengers')}
              </Typography>
              <SearchPassengerPicker
                value={
                  passengers.length
                    ? countOnlyPassengers(
                      passengers.length,
                      RUTERO_HOLD_PASSENGER_MAX,
                    )
                    : []
                }
                onChange={changeTourParty}
                rules={COUNT_ONLY_PASSENGER_RULES}
                maxPassengers={RUTERO_HOLD_PASSENGER_MAX}
              />
            </Box>
          )}

          {isTours && Boolean(visibleOfferOptions?.pickups?.length) && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('travelServices.tours.pickup')}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {visibleOfferOptions?.pickups?.map((pickup) => (
                  <Chip
                    key={pickup.id}
                    clickable
                    color={pickup.id === pickupId ? 'primary' : 'default'}
                    variant={pickup.id === pickupId ? 'filled' : 'outlined'}
                    label={[pickup.name, pickup.type].filter(Boolean).join(' · ')}
                    onClick={() => {
                      if (pickup.id === pickupId) return;
                      setSettledTourOptions(
                        tourOptionsAwaitingPickup(visibleOfferOptions),
                      );
                      offerOptions.reset();
                      setPickupId(pickup.id);
                      setSessionId('');
                      setTourAgeInputSessionId('');
                      setTourUndatedAgeInputs(tourQuoteAgeDrafts(partySnapshot.party));
                      setScopedTourQuoteRequirements(null);
                      setTourTicketQuantities([]);
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {isTours && visibleOfferOptions?.sessions && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('travelServices.tours.session')}
              </Typography>
              {visibleOfferOptions.sessions.length === 0 ? (
                <Alert severity="warning">{t('travelServices.tours.noSessions')}</Alert>
              ) : (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {visibleOfferOptions.sessions.map((session) => (
                    <Chip
                      key={session.id}
                      clickable
                      color={session.id === sessionId ? 'primary' : 'default'}
                      variant={session.id === sessionId ? 'filled' : 'outlined'}
                      label={tourSessionLabel(session)}
                      onClick={() => {
                        if (session.id === sessionId) return;
                        setTourSelectionUnavailable(false);
                        setSessionId(session.id);
                        setScopedTourQuoteRequirements(null);
                        setTourAgeInputSessionId(
                          session.quoteRequirements?.includes('ages')
                            ? session.id
                            : '',
                        );
                        setTourUndatedAgeInputs(tourQuoteAgeDrafts(partySnapshot.party));
                        setTourTicketQuantities([]);
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {isTours && selectedTourSession && (
            <TourTicketQuantities
              session={selectedTourSession}
              selection={tourTicketQuantities}
              onChange={(id, delta) => {
                setTourTicketQuantities((current) => changeTourTicketQuantity(current, id, delta));
              }}
            />
          )}

          {isTours && tourAgeInputsVisible && selectedTourSession && (
            <Box>
              <Typography variant="subtitle2">
                {t('travelServices.tours.passengerAgesForQuote')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {t(selectedTourSession.date
                  ? 'travelServices.tours.passengerAgesForQuoteHint'
                  : 'travelServices.tours.passengerAgesWithoutDateHint')}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {selectedTourSession.date ? passengers.map((pax, index) => {
                  const age = tourPassengerAgeOnServiceDate(
                    pax.birthDate,
                    selectedTourSession.date,
                  );
                  const selectedTraveler = currentCaseTravelers?.items.find(
                    (traveler) => traveler.id === pax.travelerId,
                  ) ?? null;
                  return (
                    <Box
                      key={`tour-quote-passenger-${index}`}
                      border={1}
                      borderColor="divider"
                      borderRadius={1}
                      p={1.5}
                    >
                      <PassengerRow
                        roleLabel={t('travelServices.booking.paxNumber', {
                          number: index + 1,
                        })}
                        pax={pax}
                        age={age ?? undefined}
                        classificationKnown={false}
                        fields={[{ field: 'birthDate', level: 'quote' }]}
                        columns={{ xs: 12, sm: 6 }}
                        fieldError={(field) =>
                          field === 'birthDate' && age === null
                        }
                        onChange={(partial) => setPax(index, partial)}
                        defaultCountry={agencyCountry}
                        showIdentityFields={false}
                        travelers={currentCaseTravelers?.items ?? []}
                        selectedTraveler={selectedTraveler}
                        travelerDisabled={(traveler) =>
                          travelerAlreadyAssigned(traveler.id, passengers, index)
                        }
                        onTravelerChange={(traveler) =>
                          selectTraveler(index, traveler, ['birthDate'])
                        }
                        disabled={savingProfiles}
                      />
                    </Box>
                  );
                }) : passengers.map((_pax, index) => {
                  const age = tourQuoteAge(tourUndatedAgeInputs[index]);
                  return (
                    <TextField
                      key={`tour-quote-age-${index}`}
                      type="number"
                      size="small"
                      fullWidth
                      required
                      label={t('travelServices.tours.passengerAge', {
                        number: index + 1,
                      })}
                      value={tourUndatedAgeInputs[index] ?? ''}
                      error={age === null}
                      helperText={age === null
                        ? t('travelServices.tours.passengerAgeInvalid')
                        : undefined}
                      onChange={(event) => {
                        const { value } = event.target;
                        setTourUndatedAgeInputs((current) =>
                          passengers.map((_, passengerIndex) => {
                            const { [passengerIndex]: existingValue } = current;
                            return passengerIndex === index
                              ? value
                              : existingValue ?? '';
                          }));
                      }}
                      inputProps={{
                        min: 0,
                        max: 120,
                        step: 1,
                        onWheel: releaseNumberInputOnWheel,
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {isTours && tourSelectionUnavailable && (
            <Alert severity="warning">
              {t('travelServices.tours.selectionUnavailable')}
            </Alert>
          )}

          {isTours && apiErrorCode(optionsPreview.error) === 'QUOTE_INPUT_REQUIRED' && (
            <Alert severity="warning">
              {t('travelServices.tours.quoteInputRequired')}
            </Alert>
          )}

          {/* Sin nada que elegir el texto del OPERADOR se muestra igual (una
                restricción del producto no depende de que haya picker); lo que
                no existe es un aviso inventado por nosotros. */}
          {optionsRender.standaloneMessage && (
            <Typography variant="body2" color="text.secondary">
              {optionsRender.standaloneMessage}
            </Typography>
          )}

          {/* El 400 del conector sin opciones por oferta no llega acá: esto es
                una falla real (el proveedor caído, un timeout). No corta la
                reserva —sin elección el proveedor asigna lo suyo— pero se dice,
                o el asesor no sabría por qué no puede elegir. */}
          {optionsRender.notice === 'unavailable' && (
            <Alert severity="info">
              {t('travelServices.booking.offerOptionsUnavailable')}
            </Alert>
          )}

          {/* El operador rechazó la selección al cotizarla (antes de
                reservar): motivo normalizado por el hub + su texto. Es un
                estado, no un error: se corrige la selección. Cualquier oferta
                con opciones puede recibirlo, sea cual sea su vertical, y si el
                hold queda bloqueado el modal SIEMPRE dice por qué. */}
          {previewDisplay.notice === 'rejected' && optionsPreview.data?.rejection && (
            <Alert severity="warning">
              <Typography variant="body2">
                {optionsPreview.data.rejection.reason === 'UNCOVERED_PASSENGERS' &&
                  !optionsPreview.data.rejection.missingPassengers
                  ? t('travelServices.booking.selectionRejected.UNCOVERED_PASSENGERS_UNKNOWN')
                  : t(
                    `travelServices.booking.selectionRejected.${optionsPreview.data.rejection.reason}`,
                    { count: optionsPreview.data.rejection.missingPassengers ?? 0 },
                  )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {optionsPreview.data.rejection.message}
              </Typography>
            </Alert>
          )}
        </>
      );

    case 'passengers': {
      /**
       * La fila es UNA sola, y es la misma para el titular y para los
       * acompañantes: rol, nombre, apellido y lo que la oferta declara para
       * TODOS. Lo que declara solo para el contacto no es un campo más del
       * pasajero 1 — es un requisito de la reserva — y por eso vive en su
       * propio bloque, debajo de las filas (#2374).
       */
      /* Lo que el bloque del titular dibuja: lo que la oferta le pide SOLO a
         él, menos lo que ya tiene control propio arriba (su vuelo, que vive
         junto al horario de su tramo). La MISMA expresión decide si el bloque
         existe y qué dibuja: con dos, el vuelo aparecía dos veces —una en
         Horarios y otra acá— o el título quedaba solo sobre nada. */
      const { row: rowFields, contact: contactFields } = passengerFormFields(
        { ...formDeclaredFields, all: passengerRowDeclaredFields },
        holderFieldsRenderedApart,
      );
      const partyGroups = committedPartyGroups(partySnapshot.party);
      const birthDateAgeReconciliation = reconcileTourBirthDateAges(
        frozenTourQuote,
        passengers,
      );
      const birthDateChangesQuotedAge =
        birthDateAgeReconciliation.mismatchedIndexes.length > 0;
      const requoteTourWithBirthDateAges = () => {
        if (!birthDateAgeReconciliation.ages) return;
        setTourAgeInputSessionId(sessionId);
        setFrozenTourQuote(null);
        setTourBookingFlow(() => reviseTourAvailability(defaultAttemptIdFactory));
      };
      const partyLabel = partyGroups
        .map((group) => {
          const label = t(`travelServices.booking.partyGroup_${group.type}`, {
            count: group.count,
          });
          return group.ages.length
            ? t('travelServices.booking.partyGroupWithAges', {
              group: label,
              ages: group.ages.join(', '),
            })
            : label;
        })
        .join(' + ');

      return (
        <>
          {selectedCase && (
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                {t('travelServices.booking.rosterHint')}
              </Typography>
              <Button
                size="small"
                disabled={caseChangeLocked}
                onClick={() => setRosterOpen(true)}
              >
                {t('travelServices.booking.manageRoster')}
              </Button>
            </Box>
          )}
          {selectedCase && caseTravelers.isLoading && (
            <Alert severity="info">
              {t('travelServices.booking.rosterLoading')}
            </Alert>
          )}
          {selectedCase && caseTravelers.isError && (
            <Alert
              severity="warning"
              action={(
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => caseTravelers.refetch()}
                >
                  {t('common.retry')}
                </Button>
              )}
            >
              {t('travelServices.booking.rosterLoadError')}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2">
              {t('travelServices.booking.passengers')}
            </Typography>
            {/* La composición que se está honrando, dicha en palabras y salida
                de la MISMA lista que dibuja las filas: un pasajero de más se
                ve acá antes que contando filas. */}
            {partyLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
              >
                {`${t(isTours
                  ? 'travelServices.booking.partyFromQuote'
                  : 'travelServices.booking.partyFromSearch', {
                  party: partyLabel,
                })} · ${t('travelServices.booking.partyChangeHint')}`}
              </Typography>
            )}
            {birthDateChangesQuotedAge && (
              <Alert
                severity="warning"
                action={(
                  <Button
                    color="inherit"
                    size="small"
                    onClick={requoteTourWithBirthDateAges}
                  >
                    {t('travelServices.tours.requoteCorrectedAges')}
                  </Button>
                )}
              >
                {t('travelServices.tours.birthDateQuoteMismatch')}
              </Alert>
            )}
            <Box display="flex" flexDirection="column" gap={1}>
              {passengers.map((pax, index) => {
                const fields = bookingPartyRowFields(
                  rowFields,
                  partySnapshot.party[index],
                );
                const isContact = index === contactIndex;
                const selectedTraveler = currentCaseTravelers?.items.find(
                  (traveler) => traveler.id === pax.travelerId,
                ) ?? null;
                const profilePatch = selectedTraveler
                  ? canonicalTravelerPatch(pax, selectedTraveler)
                  : null;
                const canSaveInlineTraveler =
                  inlineCreatedCaseId === selectedCase?.id &&
                  !pax.travelerId &&
                  Boolean(travelerCreationInput(pax));
                const baseRoleLabel = isContact
                  ? t(isCars
                    ? 'travelServices.booking.driverRow'
                    : 'travelServices.booking.contactRow', {
                    number: index + 1,
                  })
                  : t('travelServices.booking.paxNumber', {
                    number: index + 1,
                  });
                const passengerRoomIndex = partySnapshot.party[index]?.roomIndex;
                return (
                  <Box
                    key={`${passengerDraft}-${index}`}
                    border={1}
                    borderColor={isContact ? 'primary.main' : 'divider'}
                    borderRadius={1}
                    p={1.5}
                  >
                    <PassengerRow
                      roleLabel={
                        passengerRoomIndex === undefined
                          ? baseRoleLabel
                          : t('travelServices.circuits.quote.passengerRoom', {
                            passenger: baseRoleLabel,
                            room: passengerRoomIndex + 1,
                          })
                      }
                      pax={pax}
                      identityDocuments={vertical === 'transfers'}
                      age={partySnapshot.party[index]?.age}
                      classificationKnown={
                        partySnapshot.party[index]?.classificationKnown !== false
                      }
                      fields={fields}
                      renderedApart={
                        isContact ? holderFieldsRenderedApart : undefined
                      }
                      columns={rowColumns}
                      fieldError={(field) =>
                        (tourQuoteBirthDatesActive &&
                          field === 'birthDate' &&
                          tourPassengerAgeOnServiceDate(
                            pax.birthDate,
                            frozenTourQuote?.serviceDate,
                          ) === null) ||
                        fieldError(field, isContact, index)
                      }
                      onChange={(partial) => {
                        setPax(index, partial);
                      }}
                      defaultCountry={agencyCountry}
                      travelers={currentCaseTravelers?.items ?? []}
                      selectedTraveler={selectedTraveler}
                      travelerDisabled={(traveler) =>
                        travelerAlreadyAssigned(traveler.id, passengers, index)
                      }
                      onTravelerChange={(traveler) => selectTraveler(index, traveler)}
                      canChooseContact={!isCars}
                      isContact={isContact}
                      onChooseContact={() =>
                        setPassengers((current) => withBookingContact(
                          current,
                          index,
                          {
                            travelers: currentCaseTravelers?.items ?? [],
                            identityDocuments: vertical === 'transfers',
                            contactFields: new Set(
                              formDeclaredFields.contact.map(({ field }) => field),
                            ),
                            visibleFields: visibleFieldsForPassenger,
                          },
                        ))
                      }
                      disabled={savingProfiles}
                    />
                    {canSaveInlineTraveler && (
                      <FormControlLabel
                        sx={{ mt: 0.5, ml: 0, alignItems: 'flex-start' }}
                        control={(
                          <Checkbox
                            size="small"
                            checked={inlineTravelerSavePreferences[index] !== false}
                            disabled={savingProfiles || createTraveler.isLoading}
                            onChange={(event) =>
                              setInlineTravelerSavePreferences((current) => ({
                                ...current,
                                [index]: event.target.checked,
                              }))
                            }
                          />
                        )}
                        label={(
                          <Box pt={0.25}>
                            <Typography variant="caption" display="block">
                              {t('travelServices.booking.saveTravelerToCase', {
                                traveler: `${pax.firstName.trim()} ${pax.lastName.trim()}`,
                              })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('travelServices.booking.saveTravelerToCaseHint')}
                            </Typography>
                          </Box>
                        )}
                      />
                    )}
                    {profilePatch && selectedTraveler && (
                      <FormControlLabel
                        sx={{ mt: 0.5, ml: 0 }}
                        control={(
                          <Checkbox
                            size="small"
                            checked={shouldSaveTravelerProfile(
                              selectedTraveler,
                              profilePatch,
                            )}
                            disabled={savingProfiles || updateTraveler.isLoading}
                            onChange={(event) => setProfileSavePreferences(
                              (current) => ({
                                ...current,
                                [selectedTraveler.id]: event.target.checked,
                              }),
                            )}
                          />
                        )}
                        label={(
                          <Typography variant="caption">
                            {t('travelServices.booking.saveChangesToProfile', {
                              traveler: `${selectedTraveler.firstName} ${selectedTraveler.lastName}`,
                            })}
                          </Typography>
                        )}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
            {passengerFieldsNotice && (
              <Typography variant="caption" color="text.secondary" display="block">
                {passengerFieldsNotice}
              </Typography>
            )}
            {/* El proveedor los valida al confirmar, pero los pasajeros viajan
                una sola vez: se completan y bloquean antes del hold. */}
            {confirmValidated.length > 0 && (
              <Typography variant="caption" color="warning.main" display="block">
                {t('travelServices.booking.confirmValidatedNotice', {
                  fields: fieldLabels(confirmValidated),
                })}
              </Typography>
            )}
          </Box>

          {/* Lo que la oferta pide SOLO del titular. No es un campo más de su
              fila: es un requisito de la reserva, y el rótulo dice por qué se
              lo estamos pidiendo a él. */}
          {contactFields.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t(
                  isCars
                    ? 'travelServices.booking.driverContactSection'
                    : 'travelServices.booking.holderContactSection',
                )}
              </Typography>
              <Grid container spacing={1.5} alignItems="flex-start">
                {contactFields.map(({ field, level }) => (
                  <Grid
                    item
                    {...fieldColumns(field)}
                    key={`${passengerDraft}-${field}`}
                  >
                    <PassengerFieldControl
                      field={field}
                      level={level}
                      error={fieldError(field, true)}
                      value={holder?.[field]}
                      identityDocument={vertical === 'transfers' && holder ? {
                        passenger: holder,
                        traveler: currentCaseTravelers?.items.find((traveler) => traveler.id === holder.travelerId),
                        onChange: (partial) => setPax(contactIndex, partial),
                      } : undefined}
                      disabled={savingProfiles}
                      onChange={(value) =>
                        setPax(contactIndex, { [field]: value })
                      }
                      defaultCountry={agencyCountry}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* El proveedor rechazó el intento anterior por un dato que
                exige. Los controles quedan marcados; esto dice cuáles. */}
          {unresolvedReportedMissingPassengerFields.length > 0 && (
            <Alert severity="warning">
              {t('travelServices.booking.missingPassengerFields', {
                fields: unresolvedFieldLabels.join(', '),
              })}
            </Alert>
          )}

          {/* El conector exige un dato que este pedido no tiene. No es un
              campo que le falte al asesor: no hay dónde cargarlo. Se dice para
              que un rechazo del proveedor no se lea como un error nuestro. */}
          {shapeContradictions.length > 0 && (
            <Alert severity="warning">
              {t('travelServices.booking.shapeContradiction', {
                fields: passengerFieldRefLabels(
                  shapeContradictions.map((entry) => entry.ref),
                  translatePassengerField,
                ).join(', '),
              })}
            </Alert>
          )}

        </>
      );
    }

    case 'optionalItems': {
      /* Lo que el proveedor ofrece aparte del servicio, y la elección de
         cuántas unidades reservar EN el mismo hold (#2383). La lista es el
         MISMO memo que arma los códigos de `holdPayload` (una derivación,
         21.9), y la MISMA expresión decide si la sección existe —el título
         vive afuera, es el control que la pliega en mobile, y no puede quedar
         solo sobre nada—, incluida la audiencia: los importes son del
         proveedor, sin el markup de la agencia. Sin adicionales no hay
         sección ni hueco. */
      if (!optionalItemsSectionVisible({ items: optionalItems, isAgent })) {
        return null;
      }
      return (
        <BookingSectionDisclosure
          title={t('travelServices.booking.optionalItems.section')}
          collapsed={collapsedSections.includes('optionalItems')}
        >
          <OfferOptionalItems
            items={optionalItems}
            isAgent={isAgent}
            selection={optionalItemSelection}
            onChangeUnits={changeOptionalItemSelection}
            holdError={optionalItemsError}
            hidePrices={hasLuggageQuote}
          />
        </BookingSectionDisclosure>
      );
    }

    case 'conditions': {
      /* La ocupación de un auto no es del producto: no se rotula. */
      const showFixedPax = !isCars;
      // La MISMA expresión con la que el bloque decide no dibujarse: el título
      // vive afuera (es el control que la pliega en mobile) y no puede quedar
      // solo sobre nada.
      if (!hasBookingConditions({ conditions: preHoldOffer?.conditions, showFixedPax })) {
        return null;
      }
      return (
        <BookingSectionDisclosure
          title={t('travelServices.booking.conditionsSection')}
          collapsed={collapsedSections.includes('conditions')}
        >
          <BookingConditions
            conditions={preHoldOffer?.conditions}
            passengerCount={passengers.length}
            showFixedPax={showFixedPax}
          />
        </BookingSectionDisclosure>
      );
    }

    case 'status':
      if (!booking) return null;
      return (
        <>
          {activeOperationLabelKey(booking) && (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <ActiveOperationChip booking={booking} />
            </Box>
          )}
          {/* Reembolso informado al cancelar. `pending` = el importe real se
              confirma en la liquidación del proveedor: no se muestra un número
              que después puede no coincidir. */}
          {booking.refund && (
            <Alert severity={booking.refund.state === 'error' ? 'warning' : 'info'}>
              {booking.refund.state === 'pending'
                ? t('travelServices.booking.refundPending')
                : t(`travelServices.booking.refund${
                  booking.refund.state === 'error' ? 'Error' : 'Done'
                }`, {
                  amount: formatMoney(booking.refund.amount),
                })}
            </Alert>
          )}
          <HoldRetentionNotice booking={booking} />
          <ActiveOperationAlert booking={booking} />
          {/* Cada estado raro con SU cara y SU acción: el reintento seguro, el
              sync que trae el estado, la conciliación manual y la adopción que
              se cierra sola. Nunca un rojo terminal sobre algo recuperable. */}
          {recoverySurface && (
            <BookingRecoveryCard
              spec={recoverySurface.spec}
              technicalCode={recoverySurface.technicalCode}
              reference={booking.providerReference}
              bookingId={booking.ruteroBookingId ?? booking.id}
              loading={recoverySurface.spec.action === 'sync' && sync.isLoading}
              onAction={
                recoverySurface.spec.action === 'sync' && syncCapabilityAvailable
                  ? () => syncBooking(booking.id)
                  // La resolución manual es del asesor: sin esa audiencia la
                  // tarjeta explica el estado igual y no ofrece un botón que no
                  // puede usar. El reintento no vive acá: con la reserva ya
                  // creada, reenviar el hold sería ofrecer una segunda.
                  : recoverySurface.spec.action === 'manual' && isAgent
                    ? () => setResolvingManual(true)
                    : undefined
              }
              disabledNote={
                recoverySurface.spec.action === 'retry'
                  ? retryBlockedNote(false)
                  : undefined
              }
            />
          )}
          {recoverySurface?.spec.action === 'manual' && resolvingManual && (
            <ManualReconciliationPanel
              booking={booking}
              onResolved={applyAuthoritativeBooking}
              onBusyChange={setResolvingManualBusy}
            />
          )}
        </>
      );

    case 'reserved':
      // Lo que el proveedor CONFIRMÓ al holdear, ni bien vuelve el hold — no
      // solo al reabrir la reserva desde el listado. Se autooculta si no hay
      // nada que mostrar.
      // Con su encabezado: ahora convive con el resumen del PEDIDO, y sin
      // rótulo los dos bloques se leerían como uno solo.
      // Los tramos del traslado (recogida, punto de encuentro, vehículo)
      // salen del MISMO registro autoritativo (`booking`, la fila que
      // devolvió el hold) — nunca del resumen congelado del formulario
      // (`heldTripSummary`), que es exactamente lo que §21.23 prohíbe.
      return booking ? (
        <>
          <SelectedBookingOptions booking={booking} />
          {confirmedOptionalItemsFallbackVisible({
            confirmed: booking.confirmedOptionalItems,
          }) && (
            <ConfirmedOptionalItems booking={booking} />
          )}
          <BookingLegsSection
            legs={booking.legs}
            passengers={booking.passengers}
            bookingState={booking.state}
          />
          {(booking.vertical === 'transfers' || booking.vertical === 'cars') &&
            cancellationPolicyVerdict === 'supported' &&
            cancellation.asked && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('travelServices.booking.cancellationPolicySection')}
              </Typography>
              <CancellationPolicyPanel
                view={cancellationPolicyView}
                notice={cancellation.notice}
              />
            </Box>
          )}
        </>
      ) : null;

    case 'questions':
      // El operador exige responder esto para emitir. Va después del hold
      // porque recién ahí sabemos qué pregunta. Se dibuja la MISMA lista que
      // bloquea el CTA y que arma el payload del confirm, y bajo la MISMA
      // condición que lo ofrece (`confirmAvailable`): con la reserva en
      // verificación o con una operación en vuelo no hay confirm que estas
      // respuestas puedan destrabar, y lo que hay que mirar lo dice el estado.
      // Lo tipeado no se pierde: queda en el draft de la reserva.
      if (!confirmAvailable) return null;
      return (
        <PendingQuestionsSection
          questions={pendingQuestions}
          answers={answers}
          onAnswer={answerQuestion}
          passengers={passengers}
        />
      );

    case 'confirmPending': {
      if (!booking) return null;
      // Dos fuentes, un solo encabezado: lo que la oferta declaró para el
      // confirm y todavía no está cargado, y el bloqueo DURABLE que el
      // proveedor publicó al descubrir un requisito que no había publicado
      // antes del hold. Sin nada de las dos, la sección no existe.
      const hasDurableBlock = bookingConfirmBlock(booking) !== null;
      if (pendingConfirmFields.length === 0 && !hasDurableBlock) return null;
      return (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Typography variant="subtitle2">
            {t('travelServices.booking.confirmPendingSection')}
          </Typography>
          {pendingConfirmFields.length > 0 && (
            <Alert severity="warning">
              <Box component="ul" sx={{ mb: 0, mt: 0, pl: 3 }}>
                {passengerFieldRefEntries(pendingConfirmFields, translatePassengerField).map(
                  ({ ref, label }) => (
                    <li key={ref}>{label}</li>
                  ),
                )}
              </Box>
              {/* Por qué no hay dónde cargarlos acá: los datos del pasajero
                  viajan al hub SOLO en el hold — el confirm lleva respuestas a
                  las preguntas del operador y nada más. */}
              <Typography variant="caption" display="block" mt={1}>
                {t('travelServices.booking.confirmPendingNotice')}
              </Typography>
            </Alert>
          )}
          <ConfirmPassengerContractWarning booking={booking} />
        </Box>
      );
    }

    case 'documents': {
      if (!booking) return null;
      // Mientras el veredicto del conector se resuelve no hay nada que decir
      // todavía: un encabezado solo, sin acción ni explicación, sería peor que
      // esperar un instante.
      const hasDocumentsSurface =
        ticketSurfaces.canFetch ||
        ticketSurfaces.showUnsupportedHint ||
        Boolean(ticketsQuery.data) ||
        ticketsQuery.isError ||
        voucherSurfaceVisible(booking);
      if (!hasDocumentsSurface) return null;
      return (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography variant="subtitle2">
              {t('travelServices.booking.documentsSection')}
            </Typography>
            {/* El proveedor autoritativo puede no emitir documentos: la acción
                se ofrece con un sí explícito del conector, y el "no" se explica
                en vez de desaparecer sin motivo. */}
            {ticketSurfaces.canFetch && (
              <LoadingButton
                size="small"
                variant="outlined"
                loading={ticketsQuery.isLoading}
                onClick={ticketsQuery.request}
              >
                {t('travelServices.booking.getTickets')}
              </LoadingButton>
            )}
            {ticketSurfaces.showUnsupportedHint && (
              <Typography variant="caption" color="text.secondary">
                {t('travelServices.booking.ticketsNotSupported')}
              </Typography>
            )}
          </Box>
          <VoucherAction booking={booking} />
          {ticketsQuery.data && (
            <BookingTickets
              tickets={ticketsQuery.data}
              vertical={vertical}
              isRefreshing={ticketsQuery.isLoading}
              isPolling={ticketsQuery.isPolling}
              pollingExhausted={ticketsQuery.pollingExhausted}
              fetchVerdict={ticketsVerdict}
              onRefresh={ticketsQuery.request}
            />
          )}
          {ticketsQuery.isError && (
            <Alert severity="info">
              {apiErrorMessage(ticketsQuery.error) ??
                  t('travelServices.booking.ticketsNotReady')}
            </Alert>
          )}
        </Box>
      );
    }

    case 'extras':
      // Los servicios adicionales del proveedor, sobre la reserva ya creada:
      // la MISMA sección que el detalle del listado. Los pasajeros son los del
      // REGISTRO, que son los que viajaron al hold y en ese orden — el índice
      // que se manda apunta a la lista que el hub conoce.
      if (!booking) return null;
      return (
        <BookingExtras
          bookingId={booking.id}
          verdict={extrasVerdict}
          moment={momentView.moment}
          hasRemoteBooking={Boolean(booking.ruteroBookingId)}
          passengers={booking.passengers}
        />
      );

    case 'record':
      return booking ? <BookingRecord booking={booking} /> : null;

    default:
      return null;
    }
  };

  return (
    /* Mobile: sheet a pantalla completa. El cuerpo (`DialogContent`) scrollea
       y la barra de decisión, que vive fuera de él, queda pegada al fondo de
       la pantalla — la misma lógica que la fila de cierre de las cards. */
    <Dialog
      open
      onClose={busy ? undefined : requestClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={mobileTapTargetSx}
    >
      <DialogTitle
        component="div"
        ref={headingRef}
        tabIndex={-1}
        aria-live="polite"
        sx={{ '&:focus': { outline: 'none' } }}
      >
        <BookingMomentHeader
          booking={booking}
          title={displayAuthority.titleName}
          provider={displayAuthority.provider}
        />
      </DialogTitle>
      <DialogContent ref={contentRef} dividers>
        <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
          {/* La oferta dejó de ser reservable: la salida es volver a buscar, y
              por eso el formulario no está — el aviso ocupa su lugar. */}
          {!momentView.hasBooking && !quoteFromActiveSearch && (
            <Alert severity="warning">{t('travelServices.booking.searchChanged')}</Alert>
          )}
          {!momentView.hasBooking && !tripComplete && (
            <Alert severity="warning">
              {t(`travelServices.transfers.incompleteRoundTripModal.${transferCoverage}`)}
            </Alert>
          )}
          {/* El aviso genérico de oferta vencida se calla SOLO cuando la
              tarjeta roja dice exactamente eso (provenance vencido, o el
              vencimiento FIRMADO — `terminalCardDeclaresOfferDead`, una sola
              definición): los rechazos limpios que bloquean el PEDIDO no
              vencen la oferta, así que si la tarifa se vence después el aviso
              tiene que seguir apareciendo (si no, el CTA desaparece sin
              motivo en pantalla). El texto nombra el reloj que cortó: el
              plazo de 15 min es NUESTRO, el firmado es del proveedor. */}
          {!momentView.hasBooking &&
            quoteFromActiveSearch &&
            !(
              holdErrorSurface?.kind === 'terminal' &&
              terminalCardDeclaresOfferDead(holdErrorSurface.spec)
            ) &&
            (expired || !quoteFreshForHold) && (
            <Alert severity="warning">
              {t(
                countdown.source === 'provider'
                  ? 'travelServices.booking.offerExpiredByProvider'
                  : 'travelServices.booking.provenanceExpired',
              )}
            </Alert>
          )}
          {/* El resultado raro del ENVÍO, cuando todavía no hay reserva: el
              reintento seguro con la misma clave, o el rechazo limpio en rojo
              con el texto del operador. */}
          {holdErrorSurface?.kind === 'recovery' && (
            <BookingRecoveryCard
              spec={holdErrorSurface.spec}
              technicalCode={holdErrorSurface.technicalCode}
              loading={hold.isLoading || retryHold.isLoading}
              // El reenvío solo puede salir mientras la oferta siga siendo
              // reservable: `canStartPreHold` es el guard final antes de
              // cualquier mutación de hold. Cuando no se puede, el CTA queda
              // deshabilitado CON el motivo y el camino que queda — nunca un
              // botón mudo, y nunca una tarjeta que promete lo que no ofrece.
              onAction={offerHoldable && holdAvailable ? handleHold : undefined}
              disabledNote={retryBlockedNote(offerHoldable)}
            />
          )}
          {holdErrorSurface?.kind === 'terminal' && (
            <HoldTerminalErrorCard
              spec={holdErrorSurface.spec}
              message={holdErrorSurface.message}
              providerHoldExpiresAt={holdErrorSurface.providerHoldExpiresAt}
              onSearchAgain={requestClose}
              // `RUTERO_OFFER_EXPIRED`: re-cotizar el mismo pedido produce
              // una oferta vigente sin cerrar el modal ni perder lo cargado.
              // Solo cuando el buscador sabe re-cotizar (hoy, transfers).
              onRequote={
                holdErrorSurface.spec.offersRequote && onRequote
                  ? handleRequote
                  : undefined
              }
              requoting={requoting}
            />
          )}

          {!momentView.hasBooking && offerHoldable && (
            <Autocomplete
              value={selectedCase}
              inputValue={caseSearch}
              onInputChange={(_event, value) => setCaseSearch(value)}
              onChange={(_event, value) => {
                if (caseChangeLocked) return;
                setSelectedCase(value);
                setInlineCreatedCaseId(null);
                setInlineTravelerSavePreferences({});
                travelerCreationRequestsRef.current = {};
                bookingCaseContext.setCase(value);
                setPassengers((current) => current.map((passenger) => ({
                  ...passenger,
                  travelerId: undefined,
                })));
              }}
              options={caseOptions}
              loading={casesLoading}
              disabled={caseChangeLocked}
              getOptionLabel={(option) =>
                `${t('travelServices.booking.caseNumber', {
                  id: addLeadingZeros(option.id, defaultLeadingZeros),
                })} · ${option.name} · ${option.clientFirstName} ${option.clientLastName}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={t('travelServices.booking.caseNoOptions')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('travelServices.booking.caseLabel')}
                  helperText={
                    casesError
                      ? t('travelServices.booking.caseLoadError')
                      : t('travelServices.booking.caseHint')
                  }
                  error={casesError}
                />
              )}
            />
          )}

          {!momentView.hasBooking && offerHoldable && selectedCase && (
            <Alert severity="info">
              {t('travelServices.booking.selectedCaseSummary', {
                case: addLeadingZeros(selectedCase.id, defaultLeadingZeros),
                client: `${selectedCase.clientFirstName} ${selectedCase.clientLastName}`,
              })}
            </Alert>
          )}

          {!momentView.hasBooking && offerHoldable && (
            <Button
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              disabled={caseChangeLocked}
              onClick={() => setCreateCaseOpen(true)}
            >
              {t('travelServices.booking.createCase')}
            </Button>
          )}

          {visibleMomentSections.map((section) => (
            <Fragment key={section}>{renderSection(section)}</Fragment>
          ))}
        </Box>
      </DialogContent>

      <BookingDecisionBar
        price={price.total}
        priceDetails={priceDetails}
        countdown={
          momentView.hasBooking ? (
            momentView.moment === 'reserved' ? (
              <HoldCountdown expiresAt={booking?.expiresAt} />
            ) : null
          ) : (
            // Solo mientras se está cotizando: una vez reservado, el precio ya
            // está congelado por el proveedor y el contador de la tarifa
            // confunde.
            <FareCountdown countdown={countdown} />
          )
        }
        flags={flags}
        blocked={visibleDecisionBlocked}
        translatePassengerField={translatePassengerField}
        blockedPrefixKey={
          isTourAvailabilityStage
            ? 'travelServices.tours.blockedForQuote'
            : decision.primary === 'confirm'
              ? 'travelServices.booking.blockedForConfirm'
              : 'travelServices.booking.blockedForHold'
        }
        primary={
          isTourAvailabilityStage && offerHoldable && holdAvailable
            ? {
              label: t('travelServices.tours.continueWithPassengers'),
              loading: offerOptions.isLoading || optionsPreview.isFetching,
              onClick: continueToTourPassengers,
            }
            : decision.primary === 'hold' && holdAvailable
              ? {
                label: t('travelServices.booking.hold'),
                loading:
                  hold.isLoading || retryHold.isLoading || savingProfiles,
                onClick: handleHold,
              }
              : decision.primary === 'confirm' && booking && confirmCapabilityAvailable
                ? {
                  label: t('travelServices.booking.confirm'),
                  tooltip: t('travelServices.booking.confirmTooltip'),
                  loading: confirm.isLoading,
                  onClick: () =>
                    run(
                      async () => {
                        const updated = await confirm.mutateAsync({
                          bookingId: booking.id,
                          // Solo lo respondido, con el `code` que publicó la
                          // pregunta: nada inventado para las que quedaron
                          // vacías (una respuesta falsa confirma la reserva con
                          // un dato incorrecto).
                          answers: questionAnswerPayload(pendingQuestions, answers),
                        });
                        // Confirmada, el draft ya no responde nada.
                        clearQuestionAnswersDraft(draftOwnerId, booking.id);
                        return updated;
                      },
                      'travelServices.booking.confirmError',
                      booking.id,
                    ),
                }
                : undefined
        }
        secondaryActions={
          booking && (
            <>
              {/* Sincronizar vive en UN solo lugar por vez: si la tarjeta del
                  estado ya lo ofrece —con la explicación al lado— la barra no
                  repite el mismo botón. Acá queda el de la operación activa
                  (confirmar/cancelar en verificación), que no tiene tarjeta. */}
              {booking.ruteroBookingId &&
                recoverySurface?.spec.action !== 'sync' &&
                syncCapabilityAvailable &&
                canSyncActiveOperation(booking) && (
                <LoadingButton
                  loading={sync.isLoading}
                  onClick={() => syncBooking(booking.id)}
                >
                  {t('travelServices.booking.sync')}
                </LoadingButton>
              )}
              {cancelCapabilityAvailable && canCancelBooking(booking) && (
                <Tooltip
                  title={
                    momentView.moment === 'reserved'
                      ? t('travelServices.booking.cancelHoldTooltip')
                      : ''
                  }
                >
                  <span>
                    <LoadingButton
                      color="error"
                      variant="outlined"
                      loading={cancellation.isCancelling}
                      onClick={cancellation.ask}
                    >
                      {t(
                        momentView.moment === 'reserved'
                          ? 'travelServices.booking.cancelHold'
                          : 'travelServices.booking.cancelBooking',
                      )}
                    </LoadingButton>
                  </span>
                </Tooltip>
              )}
            </>
          )
        }
        closeLabel={t('travelServices.booking.close')}
        onClose={requestClose}
        closeDisabled={busy}
      />

      {/* Cerrar con la reserva retenida tampoco: se dice hasta cuándo sigue
          viva antes de dejarla sola. El pedido se descarta SIEMPRE al cerrar:
          el modal no se desmonta (el buscador lo deja montado sin oferta), así
          que un pedido que sobreviviera reaparecería como pregunta sobre la
          reserva siguiente. */}
      <ConfirmCloseHeldBookingDialog
        intent={closeIntent}
        onKeepOpen={() => setCloseIntent(null)}
        onCloseAnyway={() => {
          setCloseIntent(null);
          onClose();
        }}
      />

      {/* Cancelar no es un click directo: la plata va ANTES del botón rojo. */}
      <CancelBookingDialog
        open={cancellation.isOpen}
        view={cancellationPolicyView}
        notice={cancellation.notice}
        isCancelling={cancellation.isCancelling}
        refusal={cancellation.refusal}
        onClose={cancellation.close}
        onConfirm={() => void cancellation.confirm()}
      />
      {selectedCase && (
        <CaseTravelersModal
          open={rosterOpen}
          caseId={selectedCase.id}
          agencyId={selectedCase.agencyId}
          hasClient={Boolean(selectedCase.clientId)}
          onClose={() => setRosterOpen(false)}
        />
      )}
      <CreateBookingCaseDialog
        open={createCaseOpen}
        onClose={() => setCreateCaseOpen(false)}
        onCreated={(caseData) => {
          if (caseChangeLocked || holdAttemptRef.current) return;
          setSelectedCase(caseData);
          setInlineCreatedCaseId(caseData.id);
          setInlineTravelerSavePreferences({});
          travelerCreationRequestsRef.current = {};
          bookingCaseContext.setCase(caseData);
          setPassengers((current) => current.map((passenger) => ({
            ...passenger,
            travelerId: undefined,
          })));
        }}
      />
    </Dialog>
  );
}
